import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminService, adminHeaders } from "../../features/admin/service";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useAlertModal } from "../../components/admin/AlertModal";
import DateRangeFilter, { type DateFilterEntry } from "../../components/admin/DateRangeFilter";
import type { PaginatedResponse, UserActivityOverview, UserMarketCoverage, UserMarketCoverageEntry, UserPendingMarketEntry, DealerReviewsData, DealerReviewEntry } from "../../features/admin/types";
import { formatIST, formatISTDateTime, parseInstant } from "../../utils/dateUtils";
import { apiClient } from "../../lib/http/apiClient";
import { useCountries, useGeoRegions } from "../../features/geo/hooks";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import CountryStateCitySelect, { type GeoSelection } from "../../components/ui/CountryStateCitySelect";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import PhoneInputField from "../../components/ui/PhoneInputField";
import { useBlobUrl, openAsBlob } from "../../hooks/useBlobUrl";
import SessionConversationDrawer from "../../components/admin/SessionConversationDrawer";

const EMPTY_MARKET_GEO: GeoSelection = {
  countryIso2: "", countryName: "", stateIso2: "", stateName: "", cityName: "",
  phoneCode: "", currency: "", currencyName: "", currencySymbol: "",
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Same business-completeness checklist as the seller-facing verification
 * gate (see missing_business_profile_fields in
 * backend/app/modules/users/service.py) — read from the same top-level
 * mirrored columns (gst_number/agency_name/offices/number_of_employees/
 * how_many_years_in_business) that step is generic to every admin-configured
 * onboarding flow, so this list stays in sync with the backend's without
 * re-deriving it from raw onboarding.steps here. Informational only — an
 * admin can still "Verify Anyway" with any/all of these missing. */
function missingSellerBusinessFields(user: any): string[] {
  const missing: string[] = [];
  if (!user?.gst_number || !user?.agency_name) missing.push("GST / company details not provided");
  if (!(Array.isArray(user?.offices) && user.offices.length)) missing.push("Office location(s) not provided");
  if (!user?.number_of_employees) missing.push("Team size not provided");
  if (!user?.pan_tan) missing.push("PAN not provided");
  if (!user?.how_many_years_in_business) missing.push("Years in business not provided");
  return missing;
}

function periodLabelToMonthInput(label: string): string {
  const m = /^([A-Za-z]{3})\w*\s+(\d{4})$/.exec(label.trim());
  if (!m) return "";
  const idx = MONTH_NAMES.findIndex((mo) => mo.toLowerCase() === m[1].slice(0, 3).toLowerCase());
  if (idx === -1) return "";
  return `${m[2]}-${String(idx + 1).padStart(2, "0")}`;
}

function monthInputToPeriodLabel(value: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return "";
  const idx = Number(m[2]) - 1;
  if (idx < 0 || idx > 11) return "";
  return `${MONTH_NAMES[idx]} ${m[1]}`;
}

function isFileUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  return /\.(pdf|png|jpe?g|gif|webp|heic|bmp|svg)(\?|$)/i.test(value.split("?")[0]);
}

function isImageUrl(value: string): boolean {
  return /\.(png|jpe?g|gif|webp|heic|bmp|svg)(\?|$)/i.test(value.split("?")[0]);
}

/** Uploaded onboarding documents (business certificates, etc.) come back as
 * presigned S3 URLs — show images inline and open PDFs/other files in a new
 * tab, instead of dumping the raw URL as unreadable text. Opens/renders via
 * a blob object URL (see useBlobUrl) so the presigned URL — full of AWS
 * credentials/signature query params — never appears in the new tab's
 * address bar or via "copy image address" on the thumbnail. */
function RenderOnboardingFileOrText({ value }: { value: string }) {
  const isImage = isFileUrl(value) && isImageUrl(value);
  const blobUrl = useBlobUrl(isFileUrl(value) ? value : null, adminHeaders());
  const [isOpening, setIsOpening] = useState(false);

  if (!isFileUrl(value)) {
    return <span className="break-words">{value}</span>;
  }

  const open = async () => {
    setIsOpening(true);
    try {
      await openAsBlob(value, adminHeaders());
    } finally {
      setIsOpening(false);
    }
  };

  if (isImage) {
    return (
      <button type="button" onClick={() => void open()} className="inline-block">
        {blobUrl ? (
          <img src={blobUrl} alt="Uploaded document" className="max-w-[160px] max-h-[160px] rounded border border-gray-700 object-contain" />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded border border-gray-700 bg-gray-800">
            <Spinner />
          </span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => void open()}
      disabled={isOpening}
      className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 underline break-all disabled:opacity-60 disabled:cursor-wait"
    >
      {isOpening && <Spinner size="sm" />}
      {isOpening ? "Opening…" : "View document"}
    </button>
  );
}

/** Same blob-open treatment as RenderOnboardingFileOrText — profile_image
 * is a presigned S3 URL too, so opening/rendering it directly would expose
 * the signed URL via the new tab's address bar or "copy image address". */
function ProfileImageThumb({ url, alt }: { url: string; alt: string }) {
  const blobUrl = useBlobUrl(url, adminHeaders());
  return (
    <button
      type="button"
      onClick={() => void openAsBlob(url, adminHeaders())}
      title="View full-size photo"
      className="shrink-0"
    >
      {blobUrl ? (
        <img
          src={blobUrl}
          alt={alt}
          className="w-10 h-10 rounded-full object-cover border border-gray-700 hover:opacity-80 transition-opacity cursor-pointer"
        />
      ) : (
        <span className="w-10 h-10 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center shrink-0">
          <Spinner />
        </span>
      )}
    </button>
  );
}

function RenderOnboardingValue({ value }: { value: any }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-gray-600 italic">—</span>;
  }
  if (typeof value === "boolean") {
    return <span>{value ? "Yes" : "No"}</span>;
  }
  if (typeof value === "string" || typeof value === "number") {
    return <RenderOnboardingFileOrText value={String(value)} />;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-600 italic">—</span>;
    if (value.every((v) => typeof v === "string" || typeof v === "number")) {
      if (value.some((v) => isFileUrl(String(v)))) {
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((v, i) => <RenderOnboardingFileOrText key={i} value={String(v)} />)}
          </div>
        );
      }
      return <span className="break-words">{value.join(", ")}</span>;
    }
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="pl-2 border-l border-gray-700">
            <RenderOnboardingValue value={v} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <span className="text-gray-500 capitalize shrink-0">{k.replace(/_/g, " ")}:</span>
            <RenderOnboardingValue value={v} />
          </div>
        ))}
      </div>
    );
  }
  return <span className="break-words">{String(value)}</span>;
}

function extractErrorMessage(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .join("; ") || fallback;
  }
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return err?.message || fallback;
}

/** Human-readable narrowed-coverage summary for a market-coverage entry.
 * Prefers state names (a state can expand to 50+ cities, too many to list)
 * and falls back to city names, then a caller-supplied empty message. */
function describeCoverage(states: string[], cities: string[], emptyLabel = "No specific state/city resolved"): string {
  if (states.length > 0) return states.join(", ");
  if (cities.length > 0) return cities.join(", ");
  return emptyLabel;
}

const ROLES = ["all", "buyer", "seller", "partner", "super_admin"];

const USER_DATE_FIELDS = [
  { value: "created_at", label: "Joined" },
  { value: "last_login_at", label: "Last Login" },
  { value: "updated_at", label: "Updated" },
];

// ─── User Detail Modal ────────────────────────────────────────────────────────

function WeightBar({ label, weight, max }: { label: string; weight: number; max: number }) {
  // Some legacy interest_graph records store a nested object (e.g. a
  // destination's per-city breakdown) instead of a flat number for a given
  // key — guard against rendering that object directly.
  const numericWeight = typeof weight === "number" && !Number.isNaN(weight) ? weight : 0;
  const pct = max > 0 ? Math.round((numericWeight / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-32 truncate text-gray-400 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-800 rounded-full h-1.5">
        <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right text-gray-500">{numericWeight.toFixed(2)}</span>
    </div>
  );
}

/** Some legacy interest_graph records nest an object (e.g. a per-city
 *  breakdown) under a key instead of a flat number — drop those entries so
 *  sorting/max-scaling isn't skewed by NaN from a stray object. */
function numericEntries(obj: Record<string, unknown>): [string, number][] {
  return Object.entries(obj).filter((e): e is [string, number] => typeof e[1] === "number");
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3 pb-1 border-b border-gray-800">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-36 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-200 break-all">{value ?? <span className="text-gray-600">—</span>}</span>
    </div>
  );
}

// Splits a stored E.164-ish value ("+919876543210") into (countryCode, number)
// when the discrete `*_country_code`/`*_number` columns aren't both present —
// covers rows saved before those columns existed. Calling codes are 1-3
// digits (e.g. "+1", "+91", "+971"), so a naive fixed-width regex silently
// steals leading digits of the national number for any non-3-digit code —
// use libphonenumber-js's calling-code table instead.
function splitE164(value?: string | null): [string, string] {
  const v = (value || "").trim();
  if (!v.startsWith("+")) return ["", v];
  try {
    const parsed = parsePhoneNumberWithError(v);
    return [`+${parsed.countryCallingCode}`, parsed.nationalNumber];
  } catch {
    const m = v.match(/^(\+\d{1,3})(\d+)$/);
    return m ? [m[1], m[2]] : [v, ""];
  }
}

// Renders country code and national number as two visually distinct pieces
// (dim badge + tabular-nums number) so admins aren't left guessing where the
// dial code ends and the actual number begins.
function PhoneDisplay({
  countryCode, number, e164,
}: { countryCode?: string | null; number?: string | null; e164?: string | null }) {
  let cc = (countryCode || "").trim();
  let num = (number || "").trim();
  if (!cc && !num && e164) [cc, num] = splitE164(e164);
  if (!cc && !num) return <span className="text-gray-600">—</span>;
  return (
    <span className="inline-flex items-baseline gap-1.5 font-mono tabular-nums">
      {cc && <span className="text-gray-500 shrink-0">{cc}</span>}
      <span className="text-gray-200">{num || "—"}</span>
    </span>
  );
}

function TagList({ items }: { items: string[] }) {
  if (!items?.length) return <span className="text-gray-600 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((t) => (
        <span key={t} className="px-2 py-0.5 bg-gray-800 text-gray-300 rounded-md text-xs">{t}</span>
      ))}
    </div>
  );
}

// signup_method / last_login_method — "password" | "otp" | "google" | "apple",
// set by backend/app/modules/auth/service.py + repository.py. Unset on
// accounts created before this tracking existed (see the admin
// backfill-signup-method endpoint), which renders as "—" here rather than
// guessing.
const AUTH_METHOD_LABELS: Record<string, string> = {
  password: "Password",
  otp: "OTP",
  google: "Google",
  apple: "Apple",
};
const AUTH_METHOD_STYLES: Record<string, string> = {
  password: "bg-indigo-500/10 text-indigo-400",
  otp: "bg-emerald-500/10 text-emerald-400",
  google: "bg-amber-500/10 text-amber-400",
  apple: "bg-gray-500/10 text-gray-300",
};
function AuthMethodBadge({ method }: { method?: string | null }) {
  if (!method) return <span className="text-gray-600">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${AUTH_METHOD_STYLES[method] || "bg-gray-800 text-gray-300"}`}>
      {AUTH_METHOD_LABELS[method] || method}
    </span>
  );
}

const CHIP_ROUTES = ["deals", "ai_itinerary", "flyers", "videos", "market_research"] as const;
type ChipRoute = typeof CHIP_ROUTES[number];

interface ChipsData { chips: string[]; welcome_title: string; welcome_sub: string; }

function ChipsPreview({ userId }: { userId: string }) {
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState<ChipRoute>("deals");
  const [cache, setCache] = useState<Partial<Record<ChipRoute, ChipsData>>>({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (r: ChipRoute) => {
    if (cache[r]) return;
    setLoading(true);
    try {
      const res = await apiClient.get<ChipsData>("/rag/chips/admin/preview", {
        params: { user_id: userId, route: r, limit: 8 },
        headers: adminHeaders(),
      });
      setCache((c) => ({ ...c, [r]: res.data }));
    } catch {
      setCache((c) => ({ ...c, [r]: { chips: [], welcome_title: "Error", welcome_sub: "Failed to load preview." } }));
    } finally {
      setLoading(false);
    }
  }, [userId, cache]);

  useEffect(() => {
    if (open) void load(route);
  }, [open, route, load]);

  const data = cache[route];

  return (
    <div className="mb-6">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-indigo-400 pb-1 border-b border-gray-800 w-full text-left hover:text-indigo-300 transition-colors"
      >
        <span className="flex-1">Personalized Chips Preview</span>
        <span className="text-gray-600 normal-case tracking-normal font-normal">{open ? "▲ collapse" : "▼ expand"}</span>
      </button>

      {open && (
        <div className="mt-3">
          {/* Route tabs */}
          <div className="flex gap-1 flex-wrap mb-3">
            {CHIP_ROUTES.map((r) => (
              <button
                key={r}
                onClick={() => setRoute(r)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  route === r ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
                }`}
              >
                {r.replace("_", " ")}
              </button>
            ))}
          </div>

          {loading && !data ? (
            <p className="text-xs text-gray-500"><Spinner /></p>
          ) : data ? (
            <div className="space-y-3">
              <div className="bg-gray-800/60 rounded-xl p-3">
                <p className="text-sm font-medium text-white mb-0.5">{data.welcome_title}</p>
                <p className="text-xs text-gray-400">{data.welcome_sub}</p>
              </div>
              {data.chips.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {data.chips.map((chip, i) => (
                    <span key={i} className="px-3 py-1 bg-indigo-900/30 border border-indigo-700/40 text-indigo-300 rounded-full text-xs">
                      {chip}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-600">No chips generated for this route.</p>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ReviewsEditor({
  draft,
  onChange,
  onSave,
  saving,
}: {
  draft: DealerReviewsData;
  onChange: (d: DealerReviewsData) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const updateSubRating = (key: keyof DealerReviewsData["sub_ratings"], value: number) => {
    onChange({ ...draft, sub_ratings: { ...draft.sub_ratings, [key]: value } });
  };

  const updateReview = (index: number, patch: Partial<DealerReviewEntry>) => {
    const reviews = draft.reviews.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange({ ...draft, reviews });
  };

  const { countries, loading: countriesLoading } = useCountries();

  const removeReview = (index: number) => {
    onChange({ ...draft, reviews: draft.reviews.filter((_, i) => i !== index) });
  };

  const addReview = () => {
    onChange({
      ...draft,
      reviews: [
        ...draft.reviews,
        { reviewer_name: "", reviewer_display_name: "", country_code: "", star_rating: 5, period_label: "", comment: "" },
      ],
    });
  };

  return (
    <div className="space-y-5">
      <Section title="Aggregate Rating">
        <div className="flex gap-3 text-sm items-center">
          <span className="w-36 shrink-0 text-gray-500">Overall Rating</span>
          <input
            type="number" min={0} max={5} step={0.1}
            value={draft.overall_rating}
            onChange={(e) => onChange({ ...draft, overall_rating: Math.min(5, Math.max(0, Number(e.target.value))) })}
            className="w-24 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-gray-200"
          />
          <span className="text-xs text-gray-500">/ 5</span>
        </div>
        {(["supplier_service", "on_time_departures", "package_quality"] as const).map((key) => (
          <div key={key} className="flex gap-3 text-sm items-center">
            <span className="w-36 shrink-0 text-gray-500 capitalize">{key.replace(/_/g, " ")}</span>
            <input
              type="number" min={0} max={5} step={0.1}
              value={draft.sub_ratings[key]}
              onChange={(e) => updateSubRating(key, Math.min(5, Math.max(0, Number(e.target.value))))}
              className="w-24 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-gray-200"
            />
            <span className="text-xs text-gray-500">/ 5</span>
          </div>
        ))}
      </Section>

      <Section title={`Reviews (${draft.reviews.length})`}>
        <div className="space-y-3">
          {draft.reviews.map((r, i) => (
            <div key={i} className="p-3 bg-gray-800/50 border border-gray-800 rounded-lg space-y-2">
              <div className="flex gap-2">
                <input
                  placeholder="Reviewer name"
                  value={r.reviewer_name}
                  onChange={(e) => updateReview(i, { reviewer_name: e.target.value })}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-200"
                />
                <input
                  placeholder="Masked display (blank = auto)"
                  value={r.reviewer_display_name}
                  onChange={(e) => updateReview(i, { reviewer_display_name: e.target.value })}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-200"
                />
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={r.country_code}
                  disabled={countriesLoading}
                  onChange={(e) => updateReview(i, { country_code: e.target.value })}
                  className="w-40 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-200"
                >
                  <option value="">{countriesLoading ? "Loading…" : "Select country"}</option>
                  {countries.map((c) => (
                    <option key={c.iso2} value={c.iso2}>{c.flag} {c.name} ({c.iso2})</option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <input
                    type="number" min={1} max={5}
                    value={r.star_rating}
                    onChange={(e) => updateReview(i, { star_rating: Math.min(5, Math.max(1, Number(e.target.value))) })}
                    className="w-14 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-200"
                  />
                  <span className="text-xs text-gray-500">/ 5</span>
                </div>
                <input
                  type="month"
                  value={periodLabelToMonthInput(r.period_label)}
                  onChange={(e) => updateReview(i, { period_label: monthInputToPeriodLabel(e.target.value) })}
                  className="w-36 bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-200"
                />
                <button onClick={() => removeReview(i)} className="ml-auto px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md">Remove</button>
              </div>
              <textarea
                placeholder="Comment"
                value={r.comment}
                onChange={(e) => updateReview(i, { comment: e.target.value })}
                className="w-full bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-200"
                rows={2}
              />
            </div>
          ))}
        </div>
        <button onClick={addReview} className="mt-2 px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors">
          + Add Review
        </button>
      </Section>

      <button
        onClick={onSave}
        disabled={saving}
        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-lg transition-colors"
      >
        {saving ? "Saving…" : "Save Reviews"}
      </button>
    </div>
  );
}

const ACCOUNT_ACTION_LABELS: Record<string, string> = {
  self_deleted: "Deleted own account",
  restored_by_login: "Restored by logging in",
  restored_by_admin: "Restored by admin",
};

/** Timeline of self-delete / restore events (users.account_status_history),
 *  newest first — lets an admin trace when and from which device an account
 *  was deleted, and who restored it. */
function AccountStatusHistory({ history }: { history: any[] }) {
  if (!history?.length) return <p className="text-sm text-gray-500">No account status changes recorded.</p>;
  return (
    <ol className="space-y-2">
      {[...history].reverse().map((h, i) => {
        const device = [h.device_platform, h.device_name].filter(Boolean).join(" · ");
        return (
          <li key={i} className="rounded-lg border border-gray-800 bg-gray-950/50 p-3 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className={`font-medium ${h.action === "self_deleted" ? "text-rose-400" : "text-green-400"}`}>
                {ACCOUNT_ACTION_LABELS[h.action] || h.action}
              </span>
              <span className="text-gray-400">{h.at ? `${formatISTDateTime(h.at)} IST` : "Unknown time"}</span>
            </div>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-400">
              {h.admin_email && (<><dt className="text-gray-500">Admin</dt><dd className="break-all">{h.admin_email}</dd></>)}
              {h.ip_address && (<><dt className="text-gray-500">IP</dt><dd className="break-all">{h.ip_address}</dd></>)}
              {device && (<><dt className="text-gray-500">Device</dt><dd className="break-all">{device}</dd></>)}
              {h.device_id && (<><dt className="text-gray-500">Device ID</dt><dd className="break-all">{h.device_id}</dd></>)}
              {h.user_agent && (<><dt className="text-gray-500">User agent</dt><dd className="break-all">{h.user_agent}</dd></>)}
              {h.note && (<><dt className="text-gray-500">Note</dt><dd>{h.note}</dd></>)}
            </dl>
          </li>
        );
      })}
    </ol>
  );
}

/** An OTP-only account's FIRST password is parked on `password_set_request`
 *  until an admin decides on it — approving copies the stored hash onto the
 *  account, rejecting leaves it OTP-only with a reason the user reads in
 *  Settings before resubmitting. The backend never sends the password or its
 *  hash here, so there is deliberately nothing to display but status,
 *  timestamps and the reviewer trail. */
function PasswordSetRequestSection({
  request, onApprove, onReject, busy, error,
}: {
  request: any;
  onApprove: () => void;
  onReject: (reason: string) => void;
  busy: boolean;
  error: string;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const pending = request?.status === "pending";

  return (
    <>
      {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
      <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={`font-medium ${
            pending ? "text-amber-400" : request?.status === "approved" ? "text-green-400" : "text-rose-400"
          }`}>
            {pending ? "Awaiting approval" : request?.status === "approved" ? "Approved" : "Rejected"}
          </span>
          <span className="text-gray-400">
            {request?.requested_at ? `Requested ${formatISTDateTime(request.requested_at)} IST` : "Unknown time"}
          </span>
        </div>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-gray-400">
          {request?.reviewed_at && (<><dt className="text-gray-500">Reviewed</dt><dd>{formatISTDateTime(request.reviewed_at)} IST</dd></>)}
          {request?.rejection_reason && (<><dt className="text-gray-500">Reason</dt><dd>{request.rejection_reason}</dd></>)}
        </dl>

        {pending && (
          rejecting ? (
            <div className="mt-3 space-y-2">
              <label className="block text-xs font-medium text-gray-400" htmlFor="password-request-reason">
                Reason
              </label>
              <textarea
                id="password-request-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="Shown to the user in Settings — e.g. could not reach you to confirm this request"
                className="w-full rounded-lg bg-gray-800 border border-gray-700 text-xs text-gray-200 p-2 focus:outline-none focus:border-gray-500"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onReject(reason.trim())}
                  disabled={busy || !reason.trim()}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-rose-600 hover:bg-rose-500 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {busy ? "Rejecting…" : "Confirm reject"}
                </button>
                <button
                  onClick={() => { setRejecting(false); setReason(""); }}
                  disabled={busy}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={onApprove}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg transition-colors"
                title="Activate the password the user submitted — this also signs them out of every device"
              >
                {busy ? "Approving…" : "Approve"}
              </button>
              <button
                onClick={() => setRejecting(true)}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-medium text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 rounded-lg transition-colors"
              >
                Reject
              </button>
            </div>
          )
        )}
      </div>
    </>
  );
}

const UserDetailModal: React.FC<{ userId: string; onClose: () => void; onEdit?: (user: any) => void; onChanged?: () => void }> = ({ userId, onClose, onEdit, onChanged }) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState("");
  const [viewingAs, setViewingAs] = useState(false);
  const [passwordRequestBusy, setPasswordRequestBusy] = useState(false);
  const [passwordRequestError, setPasswordRequestError] = useState("");

  // Re-reads the user so the section flips to its decided state (and the list
  // badge clears) straight after Approve/Reject.
  const decidePasswordRequest = async (decide: () => Promise<unknown>) => {
    setPasswordRequestBusy(true);
    setPasswordRequestError("");
    try {
      await decide();
      setUser(await adminService.getUser(userId));
      onChanged?.();
    } catch (err: any) {
      setPasswordRequestError(extractErrorMessage(err, "Failed to update the password request"));
    } finally {
      setPasswordRequestBusy(false);
    }
  };

  // Read-only "view as user": the token travels in the URL fragment so it is
  // never sent to a server or written to access logs.
  const handleViewAs = async () => {
    setViewingAs(true);
    try {
      const res = await adminService.impersonateUser(userId);
      const envUrl = (import.meta.env.VITE_WEB_APP_URL as string | undefined)?.replace(/\/$/, "");
      const base = envUrl || "https://gullivr.ai";
      const params = new URLSearchParams({ impersonate: res.access_token, user: JSON.stringify(res.user) });
      window.open(`${base}/#${params.toString()}`, "_blank", "noopener");
    } catch (err: any) {
      setRestoreError(extractErrorMessage(err, "Failed to open user view"));
    } finally {
      setViewingAs(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setRestoreError("");
    try {
      setUser(await adminService.restoreUserAccount(userId));
      setConfirmRestore(false);
      onChanged?.();
    } catch (err: any) {
      setRestoreError(extractErrorMessage(err, "Failed to restore account"));
    } finally {
      setRestoring(false);
    }
  };
  const [detailTab, setDetailTab] = useState<"profile" | "reviews" | "gates">("profile");
  const [reviewsDraft, setReviewsDraft] = useState<DealerReviewsData>({
    overall_rating: 0,
    sub_ratings: { supplier_service: 0, on_time_departures: 0, package_quality: 0 },
    reviews: [],
  });
  const [savingReviews, setSavingReviews] = useState(false);
  const [reviewsSaveMessage, setReviewsSaveMessage] = useState("");

  // Per-user "Verification Gating" exemptions — separate from the
  // role-level toggles on Admin > Settings. Only visible when the tab is
  // open (lazy-fetched) so opening any user's detail modal doesn't add an
  // extra request for the common case of never touching this tab.
  const [gateDefs, setGateDefs] = useState<{ key: string; label: string; description: string }[] | null>(null);
  const [gateBusyKey, setGateBusyKey] = useState<string | null>(null);
  const [gateError, setGateError] = useState("");
  useEffect(() => {
    if (detailTab === "gates" && gateDefs === null) {
      adminService.getGateDefinitions()
        .then((res) => setGateDefs(res.gates))
        .catch((err) => setGateError(extractErrorMessage(err, "Failed to load gate definitions")));
    }
  }, [detailTab, gateDefs]);

  const toggleUserGateExemption = async (gateKey: string, nextValue: boolean) => {
    setGateBusyKey(gateKey);
    setGateError("");
    try {
      const updated = await adminService.updateUserGateExemption(userId, gateKey, nextValue);
      setUser(updated);
    } catch (err: any) {
      setGateError(extractErrorMessage(err, "Failed to update exemption"));
    } finally {
      setGateBusyKey(null);
    }
  };

  useBodyScrollLock();

  useEffect(() => {
    setLoading(true);
    adminService.getUser(userId)
      .then(setUser)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (user?.reviews_data) {
      setReviewsDraft({
        overall_rating: user.reviews_data.overall_rating ?? 0,
        sub_ratings: {
          supplier_service: user.reviews_data.sub_ratings?.supplier_service ?? 0,
          on_time_departures: user.reviews_data.sub_ratings?.on_time_departures ?? 0,
          package_quality: user.reviews_data.sub_ratings?.package_quality ?? 0,
        },
        reviews: user.reviews_data.reviews ?? [],
      });
    }
  }, [user]);

  const isSeller = user?.role === "seller" || user?.role === "super_admin";

  // Collect displayed top-level keys so we can show extras
  const KNOWN_KEYS = new Set([
    "_id", "id", "hashed_password", "password",
    "name", "email", "phone", "whatsapp_number", "role", "is_active", "is_verified",
    "country", "city", "bio", "created_at", "last_login_at",
    "agency_name", "seller_types", "inventory_score", "seller_profile",
    "interest_graph", "preferred_countries", "preferred_cities", "preferred_tags", "preferred_activities",
    "onboarding", "metadata",
    // AI enrichment fields — belong in per-deal view, not user profile
    "ai_suggested_title", "ai_suggested_description", "enriched_summary", "ai_description",
    "profile_picture", "avatar_url", "photo_url", "logo_url", "profile_image",
    "subscription_tier", "subscription_status", "stripe_customer_id",
    // Shown in the "Account Status" section instead
    "account_disabled", "disabled_at", "disabled_by", "account_status_history",
    // Shown in the "Password Set Request" section instead
    "password_set_request",
  ]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {user?.profile_image ? (
              <ProfileImageThumb url={user.profile_image} alt={user?.name || "Profile"} />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-sm font-semibold text-gray-400 shrink-0">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white truncate">{user?.name || "User Details"}</h2>
              {user?.email && <p className="text-xs text-gray-500 mt-0.5 truncate">{user.email}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {user?.account_disabled && (
              confirmRestore ? (
                <>
                  <button
                    onClick={handleRestore}
                    disabled={restoring}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg transition-colors"
                  >
                    {restoring ? "Restoring…" : "Confirm restore"}
                  </button>
                  <button
                    onClick={() => setConfirmRestore(false)}
                    disabled={restoring}
                    className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmRestore(true)}
                  className="px-3 py-1.5 text-xs font-medium text-green-400 bg-green-500/10 hover:bg-green-500/20 rounded-lg transition-colors"
                  title="Undo the user's self-delete: re-activate login and un-hide their deals"
                >
                  Restore account
                </button>
              )
            )}
            {user && user.role !== "super_admin" && (
              <button
                onClick={handleViewAs}
                disabled={viewingAs}
                className="px-3 py-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 disabled:opacity-50 rounded-lg transition-colors"
                title="Open the web app as this user (read-only, 30 min, audited)"
              >
                {viewingAs ? "Opening…" : "View as user"}
              </button>
            )}
            {onEdit && user && (
              <button
                onClick={() => onEdit(user)}
                className="px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-lg font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex gap-1 px-5 pt-3 shrink-0">
          {(isSeller ? (["profile", "reviews", "gates"] as const) : (["profile", "gates"] as const)).map((t) => (
            <button
              key={t}
              onClick={() => setDetailTab(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                detailTab === t ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              {t === "gates" ? "Verification Gating" : t}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto p-5 flex-1">
          {detailTab === "gates" ? (
            <Section title="Verification Gating Exemptions">
              <p className="text-xs text-gray-500 mb-4">
                Exempt this one user from a specific gate below. Turning a
                toggle on here only affects this user — everyone else keeps
                following the role-level setting on Admin &gt; Settings &gt;
                Verification Gating as normal.
              </p>
              {gateError && <p className="text-sm text-red-400 mb-3">{gateError}</p>}
              {gateDefs === null ? (
                <div className="py-8 text-center text-gray-500"><Spinner /></div>
              ) : gateDefs.length === 0 ? (
                <p className="text-sm text-gray-500">No exemptable gates configured.</p>
              ) : (
                <div className="space-y-3">
                  {gateDefs
                    // Role-prefixed gates ("seller_messaging", "buyer_enquiry", …)
                    // only ever apply to that role — showing a buyer's modal the
                    // seller-only rows would just be noise they can never trigger.
                    // Gates with no role prefix (e.g. gst_website_required, which
                    // is scoped per-role internally) always show.
                    .filter((gate) => {
                      const rolePrefix = gate.key.match(/^(seller|buyer)_/)?.[1];
                      return !rolePrefix || rolePrefix === user?.role;
                    })
                    .map((gate) => {
                    const exempt = Boolean(user?.gate_exemptions?.[gate.key]);
                    const busy = gateBusyKey === gate.key;
                    return (
                      <div key={gate.key} className="flex items-start justify-between gap-4 rounded-lg border border-gray-800 bg-gray-800/40 p-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white">{gate.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{gate.description}</p>
                        </div>
                        <button
                          onClick={() => toggleUserGateExemption(gate.key, !exempt)}
                          disabled={busy}
                          className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                            exempt ? "bg-indigo-600" : "bg-gray-700"
                          }`}
                          title={exempt ? "Exempt — click to re-enable this gate for this user" : "Not exempt — click to exempt this user"}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              exempt ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>
          ) : detailTab === "reviews" ? (
            <>
              <ReviewsEditor draft={reviewsDraft} onChange={setReviewsDraft} saving={savingReviews}
                onSave={async () => {
                  setSavingReviews(true);
                  setReviewsSaveMessage("");
                  try {
                    await adminService.updateDealerReviews(userId, reviewsDraft);
                    setReviewsSaveMessage("Reviews saved successfully");
                  } catch (err: any) {
                    setReviewsSaveMessage(extractErrorMessage(err, "Failed to save reviews"));
                  } finally {
                    setSavingReviews(false);
                  }
                }}
              />
              {reviewsSaveMessage && (
                <p className={`text-sm mt-3 ${reviewsSaveMessage.includes("success") ? "text-green-400" : "text-red-400"}`}>
                  {reviewsSaveMessage}
                </p>
              )}
            </>
          ) : loading ? (
            <div className="py-16 text-center text-gray-500"><Spinner /></div>
          ) : !user ? (
            <div className="py-16 text-center text-gray-500">Failed to load user.</div>
          ) : (
            <>
              {/* Basic Info */}
              <Section title="Basic Info">
                <Field label="ID" value={user._id || user.id} />
                <Field label="Name" value={user.name} />
                <Field label="Email" value={user.email} />
                <Field label="Phone" value={<PhoneDisplay countryCode={user.phone_country_code} number={user.phone_number} e164={user.phone} />} />
                <Field label="WhatsApp" value={<PhoneDisplay countryCode={user.whatsapp_country_code} number={user.whatsapp_number} e164={user.whatsapp} />} />
                <Field label="Role" value={
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    user.role === "seller" ? "bg-cyan-500/10 text-cyan-400" :
                    user.role === "super_admin" ? "bg-amber-500/10 text-amber-400" :
                    "bg-emerald-500/10 text-emerald-400"
                  }`}>{user.role}</span>
                } />
                <Field label="Active" value={
                  <span className={user.is_active !== false ? "text-green-400" : "text-red-400"}>
                    {user.is_active !== false ? "Yes" : "No"}
                  </span>
                } />
                <Field label="Verified" value={
                  <span className={user.is_verified ? "text-green-400" : "text-gray-500"}>
                    {user.is_verified ? "Yes" : "No"}
                  </span>
                } />
                <Field label="Country" value={user.country} />
                <Field label="City" value={user.city} />
                <Field label="Bio" value={user.bio} />
                <Field label="Created" value={user.created_at ? formatIST(user.created_at) : undefined} />
                <Field label="Signed Up Via" value={<AuthMethodBadge method={user.signup_method} />} />
                <Field label="Last Login" value={user.last_login_at ? formatISTDateTime(user.last_login_at) : "Never"} />
                <Field label="Last Login Via" value={<AuthMethodBadge method={user.last_login_method} />} />
              </Section>

              {/* Account Status — self-delete / restore trail */}
              {(user.account_disabled || user.account_status_history?.length > 0) && (
                <Section title="Account Status">
                  {user.account_disabled && (
                    <div className="mb-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
                      The user deleted their own account
                      {user.disabled_at ? ` on ${formatISTDateTime(user.disabled_at)} IST` : ""}. They can't log in and their
                      deals are hidden until the account is restored.
                    </div>
                  )}
                  {restoreError && <p className="mb-3 text-sm text-red-400">{restoreError}</p>}
                  <AccountStatusHistory history={user.account_status_history || []} />
                </Section>
              )}

              {/* First-password approval gate — only OTP-only accounts that
                  actually submitted one ever have this field. */}
              {user.password_set_request && (
                <Section title="Password Set Request">
                  <PasswordSetRequestSection
                    request={user.password_set_request}
                    busy={passwordRequestBusy}
                    error={passwordRequestError}
                    onApprove={() => void decidePasswordRequest(() => adminService.approvePasswordRequest(userId))}
                    onReject={(reason) => void decidePasswordRequest(() => adminService.rejectPasswordRequest(userId, reason))}
                  />
                </Section>
              )}

              {/* Seller Info */}
              {isSeller && (
                <Section title="Seller Info">
                  <Field label="Agency" value={user.agency_name} />
                  <Field label="Seller Types" value={
                    user.seller_types?.length
                      ? <TagList items={user.seller_types} />
                      : undefined
                  } />
                  {user.inventory_score !== undefined && (
                    <div className="text-sm">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="w-36 shrink-0 text-gray-500">Inventory Score</span>
                        <div className="flex-1 bg-gray-800 rounded-full h-2">
                          <div
                            className="bg-indigo-500 h-2 rounded-full"
                            style={{ width: `${Math.min(100, Number(user.inventory_score) || 0)}%` }}
                          />
                        </div>
                        <span className="text-gray-300 text-xs">{Number(user.inventory_score).toFixed(1)}</span>
                      </div>
                    </div>
                  )}
                  {user.seller_profile && (
                    <div className="ml-0 space-y-1 pt-1">
                      {user.seller_profile.seller_subtypes?.length > 0 && (
                        <div className="flex gap-3 text-sm">
                          <span className="w-36 shrink-0 text-gray-500">Subtypes</span>
                          <TagList items={user.seller_profile.seller_subtypes} />
                        </div>
                      )}
                      {user.seller_profile.countries?.length > 0 && (
                        <div className="flex gap-3 text-sm">
                          <span className="w-36 shrink-0 text-gray-500">Countries</span>
                          <TagList items={user.seller_profile.countries} />
                        </div>
                      )}
                      {user.seller_profile.cities?.length > 0 && (
                        <div className="flex gap-3 text-sm">
                          <span className="w-36 shrink-0 text-gray-500">Cities</span>
                          <TagList items={user.seller_profile.cities} />
                        </div>
                      )}
                      {user.seller_profile.segments?.length > 0 && (
                        <div className="flex gap-3 text-sm">
                          <span className="w-36 shrink-0 text-gray-500">Segments</span>
                          <TagList items={user.seller_profile.segments} />
                        </div>
                      )}
                      {(user.seller_profile.min_group_pax !== undefined || user.seller_profile.max_group_pax !== undefined) && (
                        <Field
                          label="Group Pax"
                          value={`${user.seller_profile.min_group_pax ?? "?"} – ${user.seller_profile.max_group_pax ?? "?"}`}
                        />
                      )}
                    </div>
                  )}
                </Section>
              )}

              {/* Buyer Interests */}
              {(user.interest_graph || user.preferred_countries?.length || user.preferred_cities?.length || user.preferred_tags?.length || user.preferred_activities?.length) && (
                <Section title="Buyer Interests / Interest Graph">
                  {user.interest_graph && (
                    <>
                      {user.interest_graph.tags && Object.keys(user.interest_graph.tags).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1.5">Top Tags</p>
                          <div className="space-y-1">
                            {(() => {
                              const entries = numericEntries(user.interest_graph.tags as Record<string, unknown>);
                              const max = Math.max(...entries.map(([, w]) => w));
                              return entries
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 10)
                                .map(([tag, w]) => <WeightBar key={tag} label={tag} weight={w} max={max} />);
                            })()}
                          </div>
                        </div>
                      )}
                      {user.interest_graph.activities && Object.keys(user.interest_graph.activities).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1.5">Top Activities</p>
                          <div className="space-y-1">
                            {(() => {
                              const entries = numericEntries(user.interest_graph.activities as Record<string, unknown>);
                              const max = Math.max(...entries.map(([, w]) => w));
                              return entries
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 10)
                                .map(([act, w]) => <WeightBar key={act} label={act} weight={w} max={max} />);
                            })()}
                          </div>
                        </div>
                      )}
                      {user.interest_graph.destinations && Object.keys(user.interest_graph.destinations).length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1.5">Top Destinations</p>
                          <div className="space-y-1">
                            {(() => {
                              const entries = numericEntries(user.interest_graph.destinations as Record<string, unknown>);
                              const max = Math.max(...entries.map(([, w]) => w));
                              return entries
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 10)
                                .map(([dest, w]) => <WeightBar key={dest} label={dest} weight={w} max={max} />);
                            })()}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {user.preferred_countries?.length > 0 && (
                    <div className="flex gap-3 text-sm">
                      <span className="w-36 shrink-0 text-gray-500">Pref. Countries</span>
                      <TagList items={user.preferred_countries} />
                    </div>
                  )}
                  {user.preferred_cities?.length > 0 && (
                    <div className="flex gap-3 text-sm">
                      <span className="w-36 shrink-0 text-gray-500">Pref. Cities</span>
                      <TagList items={user.preferred_cities} />
                    </div>
                  )}
                  {user.preferred_tags?.length > 0 && (
                    <div className="flex gap-3 text-sm">
                      <span className="w-36 shrink-0 text-gray-500">Pref. Tags</span>
                      <TagList items={user.preferred_tags} />
                    </div>
                  )}
                  {user.preferred_activities?.length > 0 && (
                    <div className="flex gap-3 text-sm">
                      <span className="w-36 shrink-0 text-gray-500">Pref. Activities</span>
                      <TagList items={user.preferred_activities} />
                    </div>
                  )}
                </Section>
              )}

              {/* Onboarding */}
              {user.onboarding && (
                <Section title="Onboarding Status">
                  <Field label="Completed" value={
                    <span className={user.onboarding.completed ? "text-green-400" : "text-amber-400"}>
                      {user.onboarding.completed ? "Yes" : "No"}
                    </span>
                  } />
                  {user.onboarding.missing_fields?.length > 0 && (
                    <div className="flex gap-3 text-sm">
                      <span className="w-36 shrink-0 text-gray-500">Missing Fields</span>
                      <TagList items={user.onboarding.missing_fields} />
                    </div>
                  )}
                  {user.onboarding.steps?.length > 0 && (
                    <div className="pt-1">
                      <p className="text-xs text-gray-500 mb-2">Steps</p>
                      <div className="space-y-1">
                        {user.onboarding.steps.map((step: any) => (
                          <div key={step.key} className="flex items-center gap-2 text-xs">
                            <span className={`w-4 text-center ${step.completed ? "text-green-400" : step.skipped ? "text-gray-600" : "text-amber-400"}`}>
                              {step.completed ? "✓" : step.skipped ? "–" : "○"}
                            </span>
                            <span className={step.completed ? "text-gray-300" : step.skipped ? "text-gray-600" : "text-gray-400"}>
                              {step.label || step.key}
                            </span>
                            {step.skipped && <span className="text-gray-700 ml-1">(skipped)</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>
              )}

              {/* Metadata */}
              {user.metadata && Object.keys(user.metadata).length > 0 && (
                <Section title="Metadata">
                  <pre className="text-xs text-gray-400 bg-gray-800 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(user.metadata, null, 2)}
                  </pre>
                </Section>
              )}

              {/* Personalized Chips Preview */}
              <ChipsPreview userId={userId} />

              {/* Extra fields */}
              {(() => {
                const extras = Object.entries(user).filter(([k]) => !KNOWN_KEYS.has(k));
                if (!extras.length) return null;
                return (
                  <Section title="Additional Fields">
                    {extras.map(([k, v]) => (
                      <Field key={k} label={k} value={
                        typeof v === "object" && v !== null
                          ? <pre className="text-xs text-gray-400 bg-gray-800 rounded p-2 whitespace-pre-wrap break-all">{JSON.stringify(v, null, 2)}</pre>
                          : String(v ?? "")
                      } />
                    ))}
                  </Section>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── User Activity Modal ───────────────────────────────────────────────────────

const ACTIVITY_TABS: { key: string; label: string }[] = [
  { key: "flyers", label: "Flyer" },
  { key: "ai_itineraries", label: "AI Itinerary" },
  { key: "slides", label: "Slides" },
  { key: "videos", label: "Video" },
  { key: "deal_sessions", label: "Deal Sessions" },
  { key: "deals_created", label: "Create Deal" },
  { key: "deals_liked", label: "Deal Liked" },
  { key: "enquiries", label: "Enquiry" },
  { key: "dms", label: "DMs" },
  { key: "rag_sessions", label: "Conversations" },
  { key: "subscription", label: "Subscription" },
  { key: "all_events", label: "All Events" },
  { key: "archived_sessions", label: "Archived Sessions" },
];

function ViewConversationButton({ onClick, label = "View Session Conversation" }: { onClick: () => void; label?: string }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 px-2 py-1 rounded-md bg-gray-800 text-indigo-300 hover:text-white hover:bg-gray-700 text-[11px] font-medium transition-colors"
    >
      {label}
    </button>
  );
}

const ENQUIRY_EVENT_LABELS: Record<string, string> = {
  enquiry_sent: "Enquiry sent",
  enquiry_draft: "Enquiry form opened",
  lead_accepted: "Accepted by seller",
};

export function ActivityRow({
  category,
  item,
  onViewConversation,
  onViewDmConversation,
}: {
  category: string;
  item: Record<string, any>;
  onViewConversation?: (sessionId: string, title: string) => void;
  onViewDmConversation?: (threadId: string, title: string) => void;
}) {
  const when = item.created_at || item.updated_at || item.last_message_at;
  const whenLabel = when ? formatISTDateTime(when) : "—";

  if (category === "dms") {
    const parties = `${item.buyer_name || item.buyer_id} ↔ ${item.seller_name || item.seller_id}`;
    return (
      <div className="py-2.5 border-b border-gray-800/60 last:border-b-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white">{parties}</span>
          <span className="text-xs text-gray-500">{whenLabel}</span>
        </div>
        {item.deal_title && <p className="text-xs text-gray-500 mt-0.5">Deal: {item.deal_title}</p>}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="text-xs text-gray-400 truncate">{item.last_message ? `"${item.last_message}"` : ""}</p>
          {item.id && onViewDmConversation && (
            <ViewConversationButton label="View DM Conversation" onClick={() => onViewDmConversation(item.id, parties)} />
          )}
        </div>
      </div>
    );
  }

  if (category === "enquiries") {
    // Names resolved server-side (get_user_activity_by_category); ids are the fallback when the
    // lead / deal / seller account is gone.
    const to = item.sent_to_org && item.sent_to_person
      ? `${item.sent_to_org} (${item.sent_to_person})`
      : item.sent_to_org || item.sent_to_person || item.seller_id || "";
    return (
      <div className="py-2.5 border-b border-gray-800/60 last:border-b-0" data-testid="enquiry-activity-row">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-white truncate">{item.enquiry_name || item.lead_id || item.item_id || "Enquiry"}</span>
          <span className="text-xs text-gray-500 shrink-0">{whenLabel}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          <span className="text-xs text-gray-400">To: <span className="text-gray-300">{to || "—"}</span></span>
          {item.lead_status && <span className="px-1.5 py-0.5 bg-gray-800 text-gray-300 rounded text-[10px]">{item.lead_status}</span>}
          <span className="px-1.5 py-0.5 bg-gray-800 text-gray-500 rounded text-[10px]">{ENQUIRY_EVENT_LABELS[item.event_type] || item.event_type}</span>
        </div>
        {(item.lead_id || item.item_id) && (
          <p className="text-[10px] text-gray-600 font-mono mt-0.5 truncate">{item.lead_id ? `Enquiry ${item.lead_id}` : item.item_id}</p>
        )}
      </div>
    );
  }

  if (category === "rag_sessions" || category === "flyers" || category === "ai_itineraries" || category === "slides" || category === "videos") {
    // These 4 media tabs are served from user_conversations (_ser_conversation), same
    // session_id/name/message_count shape as rag_sessions — not the analytics-event
    // shape (item_id/item_title/event_type) the generic row below expects.
    return (
      <div className="py-2.5 border-b border-gray-800/60 last:border-b-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white">{item.name || item.session_id}</span>
          <span className="text-xs text-gray-500">{whenLabel}</span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="text-[10px] text-gray-600 font-mono truncate">{item.session_id}</p>
          {item.session_id && onViewConversation && (
            <ViewConversationButton onClick={() => onViewConversation(item.session_id, item.name || item.session_id)} />
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="text-xs text-gray-500">{item.message_count} message(s)</p>
          {(item.cost_usd > 0 || item.llm_calls > 0) && (
            <span className="text-xs text-emerald-400 font-mono" title={`${item.llm_calls} LLM call(s) · ${item.total_tokens} tokens · ${item.fal_calls} FAL/image call(s)`}>
              · ${item.cost_usd.toFixed(4)} ({item.llm_calls} LLM{item.fal_calls ? ` + ${item.fal_calls} FAL` : ""})
            </span>
          )}
        </div>
        {item.last_message_preview && (
          <p className="text-xs text-gray-400 mt-0.5 truncate">
            <span className="text-gray-600">{item.last_message_role}:</span> {item.last_message_preview}
          </p>
        )}
      </div>
    );
  }

  if (category === "deals_created") {
    return (
      <div className="py-2.5 border-b border-gray-800/60 last:border-b-0 flex items-center justify-between">
        <div>
          <span className="text-sm text-white">{item.item_title || "Untitled"}</span>
          <span className="ml-2 px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded text-[10px]">{item.item_type}</span>
          {item.status && <span className="ml-1 px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded text-[10px]">{item.status}</span>}
        </div>
        <span className="text-xs text-gray-500">{whenLabel}</span>
      </div>
    );
  }

  if (category === "deals_liked") {
    return (
      <div className="py-2.5 border-b border-gray-800/60 last:border-b-0 flex items-center justify-between">
        <div>
          <span className="text-sm text-white">{item.item_title || item.deal_id}</span>
          {item.item_type && <span className="ml-2 px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded text-[10px]">{item.item_type}</span>}
        </div>
        <span className="text-xs text-gray-500">{whenLabel}</span>
      </div>
    );
  }

  if (category === "subscription") {
    return (
      <div className="py-2.5 border-b border-gray-800/60 last:border-b-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-white">
            {item.label || item.event_type}
            {item.tier && (
              <span className="ml-2 px-1.5 py-0.5 bg-indigo-900/60 text-indigo-300 rounded text-[10px] uppercase">{item.tier}</span>
            )}
          </span>
          <span className="text-xs text-gray-500">{whenLabel}</span>
        </div>
        {item.detail && <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>}
        {item.reason && <p className="text-xs text-gray-400 mt-0.5 italic truncate">"{item.reason}"</p>}
      </div>
    );
  }

  // Generic analytics-event style row (deal_sessions, enquiries, all_events)
  return (
    <div className="py-2.5 border-b border-gray-800/60 last:border-b-0">
      <div className="flex items-center justify-between">
        <span className="text-sm text-white">{item.event_type}{item.channel ? ` · ${item.channel}` : ""}</span>
        <span className="text-xs text-gray-500">{whenLabel}</span>
      </div>
      {(item.item_title || item.item_id) && (
        <p className="text-xs text-gray-500 mt-0.5">{item.item_title || item.item_id} {item.item_type ? `(${item.item_type})` : ""}</p>
      )}
      {item.metadata?.media_type && <p className="text-xs text-gray-500 mt-0.5">Media: {item.metadata.media_type}</p>}
    </div>
  );
}

// ─── Change Account Number Modal ──────────────────────────────────────────────
//
// The account's WhatsApp/login number is where every login OTP goes, so no
// self-service path can move it — saves that try are rejected with HTTP 409
// ACCOUNT_NUMBER_LOCKED (backend users/service.py _raise_account_number_locked).
// This modal drives the ONE route that can (POST /api/admin/users/{id}/change-number).
// Users file the ask themselves via POST /api/v1/profile/request-number-change,
// which lands on the bell as `phone_change_requested` and deep-links here via
// ?number_change_user=<id>.

interface PhoneHistoryEntry {
  old_phone?: string | null;
  old_whatsapp?: string | null;
  new_phone?: string | null;
  new_whatsapp?: string | null;
  reason?: string | null;
  admin_name?: string | null;
  changed_at?: string | null;
}

const UserNumberChangeModal: React.FC<{ userId: string; onClose: () => void; onChanged?: () => void }> = ({
  userId, onClose, onChanged,
}) => {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newNumber, setNewNumber] = useState("");
  const [reason, setReason] = useState("");
  const [notifyUser, setNotifyUser] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Populated from a 409's `detail.conflicting_user` so the admin sees WHO owns
  // the number, inline, instead of a generic "already in use".
  const [conflict, setConflict] = useState<{ id: string; name: string; email: string } | null>(null);
  const [done, setDone] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useBodyScrollLock();

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    adminService.getUser(userId)
      .then((u) => {
        setUser(u);
        // Pre-fill from the user's own request when there is one, so the admin
        // usually just reads the reason and hits Confirm.
        const requested = u?.pending_phone_change_request?.requested_phone;
        if (requested) setNewNumber(String(requested));
      })
      .catch(() => setLoadError("Failed to load user."))
      .finally(() => setLoading(false));
  }, [userId]);

  const pending = user?.pending_phone_change_request as
    | { requested_phone?: string; requested_whatsapp?: string; reason?: string; requested_at?: string }
    | undefined;
  const history: PhoneHistoryEntry[] = Array.isArray(user?.phone_number_history)
    ? [...user.phone_number_history].reverse()
    : [];

  const submit = async () => {
    setError("");
    setConflict(null);
    const [cc, national] = splitE164(newNumber);
    if (!cc || !national) {
      setError("Enter the new number in full, including its country code.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await adminService.changeUserNumber(userId, {
        new_phone_country_code: cc,
        new_phone_number: national,
        reason: reason.trim(),
        notify_user: notifyUser,
      });
      // phone*/whatsapp* are kept in sync, so one number covers both.
      setUser((prev: any) => ({
        ...prev,
        phone: result.phone,
        whatsapp: result.whatsapp,
        pending_phone_change_request: null,
        phone_number_history: [...(prev?.phone_number_history || []), result.history_entry],
      }));
      setDone(true);
      onChanged?.();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.conflicting_user) {
        setConflict(detail.conflicting_user);
      } else if (typeof detail === "string") {
        setError(detail);
      } else if (Array.isArray(detail)) {
        // FastAPI 422 body — the schema rejected the number's format.
        setError(detail.map((d: any) => d?.msg).filter(Boolean).join(" ") || "Invalid number.");
      } else {
        setError(detail?.message || "Failed to change the number.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-xl shadow-2xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Change Number{user?.name ? ` — ${user.name}` : ""}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Moves the account&apos;s WhatsApp / login number. No OTP is sent — the number is
              format-checked and must not belong to another account.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none px-2">✕</button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {loading && <p className="text-sm text-gray-400">Loading…</p>}
          {loadError && <p className="text-sm text-red-400">{loadError}</p>}

          {!loading && !loadError && (
            <>
              <div className="grid grid-cols-2 gap-4 rounded-xl bg-gray-800/50 border border-gray-700/50 p-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Current phone</p>
                  <div className="mt-1 text-sm">
                    <PhoneDisplay
                      countryCode={user?.phone_country_code}
                      number={user?.phone_number}
                      e164={user?.phone}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Current WhatsApp</p>
                  <div className="mt-1 text-sm">
                    <PhoneDisplay
                      countryCode={user?.whatsapp_country_code}
                      number={user?.whatsapp_number}
                      e164={user?.whatsapp}
                    />
                  </div>
                </div>
              </div>

              {pending?.requested_phone && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                  <p className="text-xs font-semibold text-amber-300">User requested a change</p>
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-sm text-gray-200 font-mono">{pending.requested_phone}</p>
                    <button
                      type="button"
                      title="Copy number"
                      onClick={() => {
                        navigator.clipboard?.writeText(pending.requested_phone || "").catch(() => {});
                      }}
                      className="text-[11px] text-gray-500 underline underline-offset-2 hover:text-gray-300"
                    >
                      Copy
                    </button>
                    {!done && (
                      <button
                        type="button"
                        // Fills the field directly — react-phone-number-input parses a
                        // full E.164 `value` and auto-selects the matching flag/country
                        // itself, so this is more reliable than a manual copy → paste
                        // (which the country-locked text field doesn't always re-parse).
                        onClick={() => {
                          setNewNumber(pending.requested_phone || "");
                          setConflict(null);
                          setError("");
                        }}
                        className="ml-auto text-xs font-medium text-amber-300 underline underline-offset-2 hover:text-amber-200"
                      >
                        Use this number
                      </button>
                    )}
                  </div>
                  {pending.reason && (
                    <p className="mt-1 text-xs text-gray-400">Reason: {pending.reason}</p>
                  )}
                  {pending.requested_at && (
                    <p className="mt-1 text-xs text-gray-500">
                      Requested {formatISTDateTime(pending.requested_at)}
                    </p>
                  )}
                </div>
              )}

              {done ? (
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4">
                  <p className="text-sm text-emerald-300">
                    Number updated to <span className="font-mono">{user?.phone}</span>.
                    {notifyUser ? " The user has been notified." : " The user was not notified."}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <PhoneInputField
                    label="New WhatsApp / login number"
                    // This modal's ambient `--color-border`/`--color-surface-subtle`
                    // custom properties resolve to PhoneInputField's light-theme
                    // fallback here, which otherwise renders the flag/country chip
                    // white-on-white against the dark modal — force the dark
                    // palette this modal already uses everywhere else instead.
                    className="admin-dark-phone"
                    value={newNumber}
                    onChange={(e164) => {
                      setNewNumber(e164);
                      setConflict(null);
                      setError("");
                    }}
                    placeholder="New number"
                    error={conflict ? " " : undefined}
                    required
                  />
                  {conflict && (
                    <p className="text-xs text-red-400">
                      This number belongs to{" "}
                      <span className="font-medium text-red-300">{conflict.name}</span>
                      {conflict.email ? ` (${conflict.email})` : ""}. Pick a different number, or
                      clear it off that account first.
                    </p>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1" htmlFor="number-change-reason">
                      Reason
                    </label>
                    <textarea
                      id="number-change-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={1000}
                      placeholder="Recorded in the number history — e.g. user changed SIM, verified over call"
                      className="w-full rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-200 p-2 focus:outline-none focus:border-gray-500"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    <input
                      type="checkbox"
                      checked={notifyUser}
                      onChange={(e) => setNotifyUser(e.target.checked)}
                      className="accent-blue-500"
                    />
                    Notify user of this change
                  </label>
                  {error && <p className="text-xs text-red-400">{error}</p>}
                </div>
              )}

              {history.length > 0 && (
                <div className="rounded-xl border border-gray-700/50 bg-gray-800/30">
                  <button
                    type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-300"
                  >
                    <span>Number history ({history.length})</span>
                    <span className="text-gray-500">{historyOpen ? "▲" : "▼"}</span>
                  </button>
                  {historyOpen && (
                    <ul className="px-4 pb-3 space-y-2.5">
                      {history.map((h, i) => (
                        <li key={i} className="text-xs border-t border-gray-700/50 pt-2.5">
                          <p className="font-mono text-gray-200">
                            {h.old_phone || "—"} <span className="text-gray-500">→</span> {h.new_phone || "—"}
                          </p>
                          {h.reason && <p className="text-gray-400 mt-0.5">Reason: {h.reason}</p>}
                          <p className="text-gray-500 mt-0.5">
                            {h.admin_name || "Admin"}
                            {h.changed_at ? ` · ${formatISTDateTime(h.changed_at)}` : ""}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-5 border-t border-gray-800 shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 text-sm hover:bg-gray-700">
            {done ? "Done" : "Cancel"}
          </button>
          {!done && (
            <button
              onClick={submit}
              disabled={submitting || loading || !newNumber}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-500 disabled:opacity-50"
            >
              {submitting ? "Changing…" : "Confirm change"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Seller Market Map Modal ───────────────────────────────────────────────────

const UserMarketCoverageModal: React.FC<{ userId: string; userName?: string; onClose: () => void }> = ({ userId, userName, onClose }) => {
  const [coverage, setCoverage] = useState<UserMarketCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState<string | null>(null);
  const [newMarketGeo, setNewMarketGeo] = useState<GeoSelection>(EMPTY_MARKET_GEO);
  const [addingMarket, setAddingMarket] = useState(false);
  const [disapprovingKey, setDisapprovingKey] = useState<string | null>(null);
  const [disapproveConfirm, setDisapproveConfirm] = useState<{
    countryName: string;
    key: string;
    label: string;
    opts?: { states?: string[]; cities?: string[] };
  } | null>(null);
  const { alertModal, showAlert } = useAlertModal();

  useBodyScrollLock();

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminService.getUserMarketCoverage(userId)
      .then(setCoverage)
      .catch(() => setError("Failed to load market coverage."))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleApprove = async (countryCode: string) => {
    setApproving(countryCode);
    try {
      const updated = await adminService.approveUserPendingMarket(userId, countryCode);
      setCoverage(updated);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to approve market.");
    } finally {
      setApproving(null);
    }
  };

  const handleDisapprove = async (countryName: string, key: string, opts?: { states?: string[]; cities?: string[] }) => {
    setDisapprovingKey(key);
    try {
      const updated = await adminService.disapproveSellerMarket(userId, countryName, opts);
      setCoverage(updated);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to disapprove market.");
    } finally {
      setDisapprovingKey(null);
    }
  };

  const handleApproveDisapproved = async (countryName: string, key: string, opts?: { states?: string[]; cities?: string[] }) => {
    setDisapprovingKey(key);
    try {
      const updated = await adminService.approveDisapprovedMarket(userId, countryName, opts);
      setCoverage(updated);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to approve market.");
    } finally {
      setDisapprovingKey(null);
    }
  };

  const handleAddMarket = async () => {
    const country = newMarketGeo.countryName.trim();
    if (!country) return;
    setAddingMarket(true);
    try {
      const updated = await adminService.addSellerMarket(userId, country, {
        states: newMarketGeo.stateName ? [newMarketGeo.stateName] : undefined,
        cities: newMarketGeo.cityName ? [newMarketGeo.cityName] : undefined,
      });
      setCoverage(updated);
      setNewMarketGeo(EMPTY_MARKET_GEO);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to add market. Check the selection and try again.");
    } finally {
      setAddingMarket(false);
    }
  };

  const { regions: geoRegions, loading: geoRegionsLoading } = useGeoRegions();
  const [newMarketRegionKey, setNewMarketRegionKey] = useState("");

  const handleAddRegionMarket = async () => {
    if (!newMarketRegionKey) return;
    const region = geoRegions.find((r) => r.key === newMarketRegionKey);
    if (!region) return;
    setAddingMarket(true);
    try {
      const updated = await adminService.addSellerMarket(userId, region.label);
      setCoverage(updated);
      setNewMarketRegionKey("");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to add region market.");
    } finally {
      setAddingMarket(false);
    }
  };

  const handleDisapproveRegion = async (regionKey: string) => {
    setDisapprovingKey(`region:${regionKey}`);
    try {
      const updated = await adminService.disapproveSellerMarketRegion(userId, regionKey);
      setCoverage(updated);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to disapprove region.");
    } finally {
      setDisapprovingKey(null);
    }
  };

  const handleApproveRegion = async (regionKey: string) => {
    setDisapprovingKey(`region:${regionKey}`);
    try {
      const updated = await adminService.approveDisapprovedMarketRegion(userId, regionKey);
      setCoverage(updated);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to approve region.");
    } finally {
      setDisapprovingKey(null);
    }
  };

  // Which of a pending region's ~28 countries the admin currently has picked
  // to verify — defaults to "all pending" the first time a region's pending
  // list is seen, but lets the admin deselect specific countries before
  // approving just a subset instead of the whole region at once.
  const [pendingRegionSelection, setPendingRegionSelection] = useState<Record<string, Set<string>>>({});

  const getPendingRegionSelection = (regionKey: string, pendingCodes: string[]): Set<string> => {
    return pendingRegionSelection[regionKey] ?? new Set(pendingCodes);
  };

  const togglePendingRegionCountry = (regionKey: string, pendingCodes: string[], code: string) => {
    setPendingRegionSelection((prev) => {
      const current = new Set(prev[regionKey] ?? pendingCodes);
      if (current.has(code)) current.delete(code);
      else current.add(code);
      return { ...prev, [regionKey]: current };
    });
  };

  const handleApprovePendingRegion = async (regionKey: string, codes: string[]) => {
    if (codes.length === 0) return;
    setDisapprovingKey(`region-pending:${regionKey}`);
    try {
      const updated = await adminService.approvePendingMarketRegion(userId, regionKey, codes);
      setCoverage(updated);
      setPendingRegionSelection((prev) => {
        const next = { ...prev };
        delete next[regionKey];
        return next;
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to approve region.");
    } finally {
      setDisapprovingKey(null);
    }
  };

  // Category slugs this seller sells in a country, keyed by the country's display name (title-cased
  // as returned by the deal-based aggregation) so it can be looked up case-insensitively.
  const categoriesByCountry = new Map<string, { display: string; categories: { slug: string; label: string }[] }>();
  (coverage?.category_coverage || []).forEach((c) =>
    categoriesByCountry.set(c.country.toLowerCase(), { display: c.country, categories: c.categories })
  );

  const marketsByCountry = new Map<string, UserMarketCoverageEntry>();
  (coverage?.markets || []).forEach((m) => marketsByCountry.set(m.country_name.toLowerCase(), m));

  const pendingByCountry = new Map<string, UserPendingMarketEntry>();
  (coverage?.pending_market_review || []).forEach((p) => pendingByCountry.set(p.country_name.toLowerCase(), p));

  // Union of onboarding markets (where they said they'd sell), pending markets, and deal-based category
  // coverage (where they've actually uploaded deals) — a seller may have real deals in
  // a country they never declared during onboarding, and vice versa.
  const allCountryKeys = Array.from(new Set([...marketsByCountry.keys(), ...categoriesByCountry.keys(), ...pendingByCountry.keys()])).sort();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Market Map{userName ? ` — ${userName}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Countries/cities this seller operates in (from onboarding) and the category slugs they sell there
              (from their own active deals) — the exact inputs used to hide overlapping competitor deals from
              their typed-deals feed.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-lg font-bold"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto p-5 flex-1">
          <div className="mb-4 rounded-xl border border-gray-800 bg-gray-800/40 p-3 space-y-3">
            <CountryStateCitySelect
              value={newMarketGeo}
              onChange={setNewMarketGeo}
              showCurrency={false}
              dark
            />
            <p className="text-[11px] text-gray-500">
              Country required. Add a state and/or city too to grant just that narrower market instead of the whole country.
            </p>
            <button
              onClick={handleAddMarket}
              disabled={addingMarket || !newMarketGeo.countryName.trim()}
              className="w-full px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-400 text-xs font-medium rounded-lg border border-cyan-600/30 transition-colors disabled:opacity-50"
            >
              {addingMarket ? "Adding…" : "Add Market"}
            </button>
            <div className="flex items-center gap-2 pt-1 border-t border-gray-800">
              <select
                value={newMarketRegionKey}
                onChange={(e) => setNewMarketRegionKey(e.target.value)}
                disabled={geoRegionsLoading}
                className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white disabled:opacity-50"
              >
                <option value="">
                  {geoRegionsLoading ? "Loading regions…" : "Or grant a whole region…"}
                </option>
                {geoRegions.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label} ({r.countries.length} countries)
                  </option>
                ))}
              </select>
              <button
                onClick={handleAddRegionMarket}
                disabled={addingMarket || !newMarketRegionKey}
                className="px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 text-cyan-400 text-xs font-medium rounded-lg border border-cyan-600/30 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {addingMarket ? "Adding…" : "Add Region"}
              </button>
            </div>
          </div>
          {!loading && (coverage?.regions?.length ?? 0) > 0 && (
            <div className="mb-4 space-y-2">
              {coverage!.regions.map((r) => {
                const hasPending = r.pending_country_codes.length > 0;
                const selection = getPendingRegionSelection(r.region_key, r.pending_country_codes);
                const selectedCodes = r.pending_country_codes.filter((c) => selection.has(c));
                return (
                  <div key={r.region_key} className="rounded-xl border border-cyan-800/50 bg-cyan-950/20 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-white">{r.label}</span>
                        <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-cyan-900/40 text-cyan-300">
                          Whole region — {r.country_iso2.length} countries
                        </span>
                        {hasPending && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                            {r.pending_country_codes.length} pending verification
                          </span>
                        )}
                        {r.disapproved && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-900/40 text-red-400">
                            Disapproved
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {r.disapproved ? (
                          <button
                            onClick={() => handleApproveRegion(r.region_key)}
                            disabled={disapprovingKey === `region:${r.region_key}`}
                            className="px-2.5 py-1 bg-green-600/20 hover:bg-green-600/40 text-green-400 text-xs font-medium rounded-lg border border-green-600/30 transition-colors disabled:opacity-50"
                          >
                            {disapprovingKey === `region:${r.region_key}` ? "Approving…" : "Approve whole region"}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDisapproveRegion(r.region_key)}
                            disabled={disapprovingKey === `region:${r.region_key}`}
                            className="px-2.5 py-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs font-medium rounded-lg border border-red-600/30 transition-colors disabled:opacity-50"
                          >
                            {disapprovingKey === `region:${r.region_key}` ? "Disapproving…" : "Disapprove whole region"}
                          </button>
                        )}
                      </div>
                    </div>

                    {hasPending && (
                      <div className="mt-2 pt-2 border-t border-amber-900/40">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] text-amber-400">
                            Awaiting first-time verification — deselect any country below to leave it pending,
                            then approve the rest.
                          </p>
                          <button
                            onClick={() => handleApprovePendingRegion(r.region_key, selectedCodes)}
                            disabled={disapprovingKey === `region-pending:${r.region_key}` || selectedCodes.length === 0}
                            className="ml-3 shrink-0 px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 text-[11px] font-medium rounded-lg border border-emerald-600/30 transition-colors disabled:opacity-50 whitespace-nowrap"
                          >
                            {disapprovingKey === `region-pending:${r.region_key}`
                              ? "Approving…"
                              : `Approve pending (${selectedCodes.length}/${r.pending_country_codes.length})`}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {r.country_iso2.map((code, idx) => {
                        const name = r.country_names[idx] || code;
                        const isPending = r.pending_country_codes.includes(code);
                        const isDisapproved = r.disapproved_country_codes.includes(code);
                        const countryKey = `region:${r.region_key}:${code}`;

                        if (isPending) {
                          const isSelected = selection.has(code);
                          return (
                            <button
                              key={code}
                              onClick={() => togglePendingRegionCountry(r.region_key, r.pending_country_codes, code)}
                              title={isSelected ? "Selected for approval — click to leave pending" : "Not selected — click to include in approval"}
                              className={`px-1.5 py-0.5 rounded text-[10px] border transition-colors ${
                                isSelected
                                  ? "bg-amber-500/20 text-amber-300 border-amber-600/50"
                                  : "bg-amber-950/20 text-amber-700 border-amber-900/40"
                              }`}
                            >
                              {isSelected ? "☑" : "☐"} {name}
                            </button>
                          );
                        }
                        if (isDisapproved) {
                          return (
                            <span
                              key={code}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-red-900/20 text-red-300 border-red-800/60"
                            >
                              {name}
                              <button
                                onClick={() => handleApproveDisapproved(name, countryKey)}
                                disabled={disapprovingKey === countryKey}
                                className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 ml-0.5"
                                title={`Approve ${name}`}
                              >
                                ✓
                              </button>
                            </span>
                          );
                        }
                        return (
                          <span
                            key={code}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border bg-gray-800 text-gray-300 border-gray-700"
                          >
                            {name}
                            <button
                              onClick={() => handleDisapprove(name, countryKey)}
                              disabled={disapprovingKey === countryKey}
                              className="text-red-400 hover:text-red-300 disabled:opacity-50 ml-0.5"
                              title={`Disapprove ${name}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {loading ? (
            <div className="py-16 text-center text-gray-500"><Spinner /></div>
          ) : error ? (
            <div className="py-16 text-center text-red-400">{error}</div>
          ) : allCountryKeys.length === 0 ? (
            <div className="py-16 text-center text-gray-500">No operating markets or deals found for this seller.</div>
          ) : (
            <div className="space-y-3">
              {allCountryKeys.map((key) => {
                const m = marketsByCountry.get(key);
                const p = pendingByCountry.get(key);
                const cat = categoriesByCountry.get(key);
                const displayName = m?.country_name || p?.country_name || cat?.display || key;
                const cats = cat?.categories || [];
                return (
                  <div key={key} className="rounded-xl border border-gray-800 bg-gray-800/40 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">{displayName}</span>
                      <div className="flex items-center gap-1.5">
                        {m?.disapproved && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-900/40 text-red-400">
                            Disapproved
                          </span>
                        )}
                        {(m || p) && (
                          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">
                            {m?.country_code || p?.country_code}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                      <div>
                        <span className="text-xs text-gray-500">Onboarding coverage: </span>
                        {p ? (
                          <span className="text-xs text-amber-500 font-medium bg-amber-500/10 px-1.5 py-0.5 rounded">
                            Pending Admin Verification ({p.coverage === "whole_country" ? "Whole country" : describeCoverage(p.states, p.cities)})
                          </span>
                        ) : !m ? (
                          <span className="text-xs text-gray-600 italic">Not declared during onboarding — deals-only market</span>
                        ) : m.coverage === "whole_country" ? (
                          <span className="text-xs text-emerald-400">Whole country (any city)</span>
                        ) : (
                          <span className="text-xs text-amber-400">
                            {describeCoverage(m.states, m.cities, "No specific state/city resolved")}
                          </span>
                        )}
                      </div>
                      {p && (
                        <button
                          onClick={() => handleApprove(p.country_code)}
                          disabled={approving === p.country_code}
                          className="px-3 py-1 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 text-xs font-medium rounded border border-emerald-600/30 transition-colors disabled:opacity-50 ml-3"
                        >
                          {approving === p.country_code ? "Approving…" : "Approve Market"}
                        </button>
                      )}
                    </div>

                    <div className="mt-2">
                      <span className="text-xs text-gray-500">Categories sold here (from own active deals): </span>
                      {cats.length === 0 ? (
                        <span className="text-xs text-gray-600 italic">No active deals of theirs found in this country</span>
                      ) : (
                        <span className="flex flex-wrap gap-1 mt-1">
                          {cats.map((c) => (
                            <span key={c.slug} className="px-1.5 py-0.5 bg-cyan-900/40 text-cyan-300 rounded text-[10px]">{c.label}</span>
                          ))}
                        </span>
                      )}
                    </div>

                    {m && (
                      <div className="mt-3 pt-3 border-t border-gray-800/70">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {m.disapproved ? "Whole country disapproved: " : "Disapprove coverage: "}
                          </span>
                          {m.disapproved ? (
                            <button
                              onClick={() => handleApproveDisapproved(m.country_name, `${m.country_code}:whole`)}
                              disabled={disapprovingKey === `${m.country_code}:whole`}
                              className="px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 text-[11px] font-medium rounded border border-emerald-600/30 transition-colors disabled:opacity-50"
                            >
                              {disapprovingKey === `${m.country_code}:whole` ? "Approving…" : "Approve whole country"}
                            </button>
                          ) : (
                            <button
                              onClick={() => setDisapproveConfirm({
                                countryName: m.country_name,
                                key: `${m.country_code}:whole`,
                                label: `the entire ${m.country_name} market`,
                              })}
                              disabled={disapprovingKey === `${m.country_code}:whole`}
                              className="px-2.5 py-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 text-[11px] font-medium rounded border border-red-600/30 transition-colors disabled:opacity-50"
                            >
                              {disapprovingKey === `${m.country_code}:whole` ? "Disapproving…" : "Disapprove whole country"}
                            </button>
                          )}
                        </div>
                        {m.coverage !== "whole_country" && (m.cities.length > 0 || m.states.length > 0) && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {m.states.map((s) => {
                              const chipKey = `${m.country_code}:state:${s}`;
                              const isDisapproved = m.disapproved_states.includes(s);
                              return (
                                <span key={chipKey} className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${isDisapproved ? "bg-red-900/20 text-red-300 border-red-800/60" : "bg-gray-800 text-gray-300 border-gray-700"}`}>
                                  {s}
                                  {isDisapproved ? (
                                    <button
                                      onClick={() => handleApproveDisapproved(m.country_name, chipKey, { states: [s] })}
                                      disabled={disapprovingKey === chipKey}
                                      className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 ml-0.5"
                                      title={`Approve state ${s}`}
                                    >
                                      ✓
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => setDisapproveConfirm({
                                        countryName: m.country_name,
                                        key: chipKey,
                                        label: `state "${s}" in ${m.country_name}`,
                                        opts: { states: [s] },
                                      })}
                                      disabled={disapprovingKey === chipKey}
                                      className="text-red-400 hover:text-red-300 disabled:opacity-50 ml-0.5"
                                      title={`Disapprove state ${s}`}
                                    >
                                      ×
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                            {m.cities.map((c) => {
                              const chipKey = `${m.country_code}:city:${c}`;
                              const isDisapproved = m.disapproved_cities.includes(c);
                              return (
                                <span key={chipKey} className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${isDisapproved ? "bg-red-900/20 text-red-300 border-red-800/60" : "bg-gray-800 text-gray-300 border-gray-700"}`}>
                                  {c}
                                  {isDisapproved ? (
                                    <button
                                      onClick={() => handleApproveDisapproved(m.country_name, chipKey, { cities: [c] })}
                                      disabled={disapprovingKey === chipKey}
                                      className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 ml-0.5"
                                      title={`Approve city ${c}`}
                                    >
                                      ✓
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => setDisapproveConfirm({
                                        countryName: m.country_name,
                                        key: chipKey,
                                        label: `city "${c}" in ${m.country_name}`,
                                        opts: { cities: [c] },
                                      })}
                                      disabled={disapprovingKey === chipKey}
                                      className="text-red-400 hover:text-red-300 disabled:opacity-50 ml-0.5"
                                      title={`Disapprove city ${c}`}
                                    >
                                      ×
                                    </button>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {alertModal}
      {disapproveConfirm && (
        <ConfirmModal
          title="Disapprove Market Coverage"
          description={`Disapprove ${disapproveConfirm.label} for this seller?`}
          confirmLabel="Disapprove"
          confirmClass="bg-red-600 hover:bg-red-500"
          irreversible={false}
          busy={disapprovingKey === disapproveConfirm.key}
          busyLabel="Disapproving…"
          onConfirm={async () => {
            const { countryName, key, opts } = disapproveConfirm;
            await handleDisapprove(countryName, key, opts);
            setDisapproveConfirm(null);
          }}
          onCancel={() => setDisapproveConfirm(null)}
        />
      )}
    </div>
  );
};

export const UserActivityModal: React.FC<{ userId: string; userName?: string; onClose: () => void }> = ({ userId, userName, onClose }) => {
  const [overview, setOverview] = useState<UserActivityOverview | null>(null);
  const [tab, setTab] = useState("flyers");
  // `tab` records which tab a page of rows belongs to, so a page is only ever shown under its own tab.
  const [data, setData] = useState<{ tab: string; items: Record<string, any>[]; total: number; page: number; page_size: number } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [loading, setLoading] = useState(false);
  const activityRequest = useRef(0);
  const [deletedSessions, setDeletedSessions] = useState<{
    session_id: string;
    source: string;
    created_at: string;
    last_message_at: string;
    message_count: number;
    archived_at: string;
  }[]>([]);
  const [deletedTotal, setDeletedTotal] = useState(0);

  const [conversation, setConversation] = useState<{ kind: "session" | "dm"; sessionId: string; title: string } | null>(null);
  const openConversation = (sessionId: string, title: string) => setConversation({ kind: "session", sessionId, title });
  const openDmConversation = (threadId: string, title: string) => setConversation({ kind: "dm", sessionId: threadId, title });
  const closeConversation = useCallback(() => setConversation(null), []);

  useBodyScrollLock();

  useEffect(() => {
    adminService.getUserActivityOverview(userId).then(setOverview).catch(console.error);
  }, [userId]);

  useEffect(() => {
    // Every tab/page change supersedes any request still in flight: a slow response for an
    // earlier tab must never land on (or paginate) the tab now open.
    const id = ++activityRequest.current;
    setData(null);
    if (tab === "archived_sessions") {
      setDeletedSessions([]);
      setDeletedTotal(0);
      setLoading(true);
      adminService.getUserDeletedSessions(userId, { page, page_size: pageSize })
        .then((res) => {
          if (id !== activityRequest.current) return;
          setDeletedSessions(res.sessions);
          setDeletedTotal(res.total ?? res.sessions.length);
        })
        .catch((e) => {
          if (id === activityRequest.current) console.error(e);
        })
        .finally(() => {
          if (id === activityRequest.current) setLoading(false);
        });
      return;
    }
    setLoading(true);
    adminService.getUserActivityCategory(userId, tab, { page, page_size: pageSize })
      .then((res) => {
        if (id === activityRequest.current) setData({ ...res, tab });
      })
      .catch((e) => {
        if (id === activityRequest.current) console.error(e);
      })
      .finally(() => {
        if (id === activityRequest.current) setLoading(false);
      });
  }, [userId, tab, page, pageSize]);

  const tabData = data && data.tab === tab ? data : null;

  useEffect(() => { setPage(1); }, [pageSize]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-3xl shadow-2xl flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Activity{userName ? ` — ${userName}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Per-user event history across the platform</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-lg font-bold"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 flex-wrap px-5 pt-3 shrink-0">
          {ACTIVITY_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                tab === t.key ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              {t.label}{overview && (overview as unknown as Record<string, number>)[t.key] !== undefined ? ` (${(overview as unknown as Record<string, number>)[t.key]})` : ""}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 flex-1">
          {tab === "archived_sessions" ? (
            loading ? (
              <div className="py-16 text-center text-gray-500"><Spinner /></div>
            ) : deletedSessions.length === 0 ? (
              <div className="py-16 text-center text-gray-500">No archived sessions found.</div>
            ) : (
              <div>
                {deletedSessions.map((s) => (
                  <div key={s.session_id} className="flex items-start gap-3 py-3 border-b border-gray-800">
                    <span className="mt-1 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-300">
                        Session <span className="font-mono text-xs text-gray-400">{s.session_id}</span> auto-archived
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {s.message_count} messages · last active {s.last_message_at ? (parseInstant(s.last_message_at) ?? new Date(s.last_message_at)).toLocaleDateString() : "unknown"} · archived {(parseInstant(s.archived_at) ?? new Date(s.archived_at)).toLocaleDateString()} · {s.source}
                      </p>
                    </div>
                    {s.session_id && (
                      <ViewConversationButton onClick={() => openConversation(s.session_id, "Archived session")} />
                    )}
                  </div>
                ))}
              </div>
            )
          ) : loading ? (
            <div className="py-16 text-center text-gray-500"><Spinner /></div>
          ) : !tabData || tabData.items.length === 0 ? (
            <div className="py-16 text-center text-gray-500">No activity found.</div>
          ) : (
            <div>
              {tabData.items.map((item, i) => (
                <ActivityRow
                  key={item.id || i}
                  category={tab}
                  item={item}
                  onViewConversation={openConversation}
                  onViewDmConversation={openDmConversation}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination (only for the open tab's own server-paged rows) */}
        {(tab === "archived_sessions" ? deletedTotal > 0 : tabData && tabData.total > 0) && (
          <div className="border-t border-gray-800 shrink-0">
            <Pagination
              variant="panel"
              page={page - 1}
              pageSize={pageSize}
              total={tab === "archived_sessions" ? deletedTotal : tabData!.total}
              onPageChange={(p) => setPage(p + 1)}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </div>
      {conversation && (
        <div onClick={(e) => e.stopPropagation()}>
          <SessionConversationDrawer
            key={`${conversation.kind}:${conversation.sessionId}`}
            userId={userId}
            sessionId={conversation.sessionId}
            title={conversation.title}
            requireReason
            fetchPage={
              conversation.kind === "dm"
                ? (p) => adminService.getUserDmConversation(userId, conversation.sessionId, p)
                : undefined
            }
            onClose={closeConversation}
          />
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const { alertModal, showAlert } = useAlertModal();

  useEffect(() => {
    const urlSearch = searchParams.get("search");
    if (urlSearch !== null && urlSearch !== search) {
      setSearch(urlSearch);
      setPage(1);
    }
  }, [searchParams]);
  const [role, setRole] = useState("all");
  // Only actually-filled company/brand/agency names — not every seller has
  // one, so this list (not free text) is what "group by company" filters on.
  const [companyName, setCompanyName] = useState("");
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  useEffect(() => {
    adminService.getCompanyNames().then(setCompanyNames).catch(() => {});
  }, []);
  const [dateFilters, setDateFilters] = useState<DateFilterEntry[]>([]);
  const [verifiedFilter, setVerifiedFilter] = useState<"all" | "verified" | "unverified">("all");
  const [restrictedFilter, setRestrictedFilter] = useState<"all" | "restricted" | "unrestricted">("all");
  const [marketPendingOnly, setMarketPendingOnly] = useState(false);
  const [passwordPendingOnly, setPasswordPendingOnly] = useState(false);
  const [selfDeletedOnly, setSelfDeletedOnly] = useState(false);
  const [bulkVerifying, setBulkVerifying] = useState(false);
  const [bulkVerifyConfirmOpen, setBulkVerifyConfirmOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("Employee credited by admin");
  const [crediting, setCrediting] = useState(false);
  const [creditMessage, setCreditMessage] = useState("");
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const [activityUser, setActivityUser] = useState<{ id: string; name?: string } | null>(null);
  // Row-level "Restore" for self-deleted accounts (two-step: Restore → Confirm).
  const [confirmRestoreId, setConfirmRestoreId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const restoreFromRow = async (id: string) => {
    setRestoringId(id);
    try {
      await adminService.restoreUserAccount(id);
      setConfirmRestoreId(null);
      await fetchUsers();
    } catch (err: any) {
      console.error(extractErrorMessage(err, "Failed to restore account"));
    } finally {
      setRestoringId(null);
    }
  };
  const [marketCoverageUser, setMarketCoverageUser] = useState<{ id: string; name?: string } | null>(null);
  const [numberChangeUserId, setNumberChangeUserId] = useState<string | null>(null);

  // Deep-link support: /admin/users?number_change_user=<id> auto-opens the
  // Change Number modal for that user — used by the admin notification bell's
  // Number Change tab for `phone_change_requested` alerts (see
  // AdminNotificationsCenter.tsx). The modal fetches the user itself (it needs
  // pending_phone_change_request + phone_number_history), so only the id is
  // stored here. One-time on mount only, same as the two below.
  useEffect(() => {
    const deepLinked = searchParams.get("number_change_user");
    if (deepLinked) setNumberChangeUserId(deepLinked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link support: /admin/users?market_user=<id>&market_user_name=<name> auto-opens
  // the Market Map modal for that seller (e.g. linked from AdminDealsPage's publish-error
  // flow). One-time on mount only — must not fight manual open/close of the modal later.
  useEffect(() => {
    const marketUserId = searchParams.get("market_user");
    if (marketUserId) {
      setMarketCoverageUser({ id: marketUserId, name: searchParams.get("market_user_name") || undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Confirm modals
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [roleChangeTarget, setRoleChangeTarget] = useState<{ user: any; newRole: "seller" | "buyer" } | null>(null);
  const [roleChanging, setRoleChanging] = useState(false);
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [onboardingUser, setOnboardingUser] = useState<any | null>(null);
  const [verifyingUser, setVerifyingUser] = useState(false);
  // Deep-link support: /admin/users?onboarding_user=<id> auto-opens the
  // Onboarding Data modal (with its Verify action) for that user — used by
  // the admin notification bell's Alerts tab for pending-verification alerts
  // (see AdminNotificationsCenter.tsx). One-time on mount only, same as above.
  useEffect(() => {
    const onboardingUserId = searchParams.get("onboarding_user");
    if (onboardingUserId) {
      adminService.getUser(onboardingUserId).then(setOnboardingUser).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [liveGstDetails, setLiveGstDetails] = useState<any | null>(null);
  const [fetchingGst, setFetchingGst] = useState(false);
  const [confirmingGst, setConfirmingGst] = useState(false);

  useBodyScrollLock(
    bulkVerifyConfirmOpen ||
    !!editingUser ||
    !!deleteTarget ||
    !!roleChangeTarget ||
    !!onboardingUser ||
    !!numberChangeUserId ||
    !!liveGstDetails
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      if (role !== "all") params.role = role;
      if (verifiedFilter !== "all") params.is_verified = verifiedFilter === "verified";
      if (restrictedFilter !== "all") params.restricted = restrictedFilter === "restricted";
      if (marketPendingOnly) params.market_pending = true;
      if (passwordPendingOnly) params.password_request_pending = true;
      if (selfDeletedOnly) params.account_disabled = true;
      if (companyName) params.company_name = companyName;
      const activeDates = dateFilters.filter((d) => d.from || d.to);
      if (activeDates.length) params.date_filters = JSON.stringify(activeDates);
      setData(await adminService.getUsers(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, role, dateFilters, verifiedFilter, restrictedFilter, marketPendingOnly, passwordPendingOnly, selfDeletedOnly, companyName]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { setPage(1); }, [pageSize]);
  useEffect(() => { setSelectedIds(new Set()); }, [data]);

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const pageIds = data?.items.map((u) => u.id) || [];
      const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(pageIds);
    });
  };

  const handleVerifySelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkActionRunning(true);
    try {
      await adminService.bulkVerifyUsers({ user_ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setBulkActionRunning(false);
    }
  };

  const handleUnverifySelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkActionRunning(true);
    try {
      await adminService.bulkUnverifyUsers(Array.from(selectedIds));
      setSelectedIds(new Set());
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setBulkActionRunning(false);
    }
  };

  // Repairs accounts showing "Never" under Last Login despite having actually
  // signed in — normal/Google/Apple login all set last_login_at correctly
  // going forward, but some accounts predate that tracking (or were created
  // via the internal contact-placeholder path). Backfills from each user's
  // most recent session. Safe to re-run.
  const [backfillingLogin, setBackfillingLogin] = useState(false);
  const handleBackfillLastLogin = async () => {
    setBackfillingLogin(true);
    try {
      const { scanned, backfilled } = await adminService.backfillLastLogin();
      alert(`Checked ${scanned} account(s) missing Last Login — fixed ${backfilled}.`);
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("Backfill failed — check the console.");
    } finally {
      setBackfillingLogin(false);
    }
  };

  // Repairs "—" showing under Signed Up Via / Last Login Via for accounts
  // that predate signup_method/last_login_method tracking. Signup is a
  // recovered fact (every pre-OAuth signup path always verified a phone
  // OTP); login is a best-effort inference (auth_provider, else password
  // hash presence, else "otp") since which channel a legacy account most
  // recently used genuinely isn't stored anywhere. Safe to re-run.
  const [backfillingAuthMethod, setBackfillingAuthMethod] = useState(false);
  const handleBackfillAuthMethod = async () => {
    setBackfillingAuthMethod(true);
    try {
      const [signup, login] = await Promise.all([
        adminService.backfillSignupMethod(),
        adminService.backfillLoginMethod(),
      ]);
      alert(
        `Signed Up Via: checked ${signup.scanned}, fixed ${signup.backfilled}.\n` +
        `Last Login Via (best-effort inferred): checked ${login.scanned}, fixed ${login.backfilled}.`
      );
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("Backfill failed — check the console.");
    } finally {
      setBackfillingAuthMethod(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminService.deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleChangeRole = async () => {
    if (!roleChangeTarget) return;
    setRoleChanging(true);
    try {
      await adminService.changeUserRole(roleChangeTarget.user.id, roleChangeTarget.newRole);
      setRoleChangeTarget(null);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setRoleChanging(false);
    }
  };

  const handleEdit = (user: any) => {
    setEditingUser(user);
    setEditForm({ name: user.name || "", role: user.role || "", is_active: user.is_active ?? true, restricted: user.restricted ?? false, is_verified: user.is_verified ?? false, verified_name: user.verified_name || "" });
    setNewPassword("");
    setCreditAmount("");
    setCreditReason("Employee credited by admin");
    setCreditMessage("");
    setResetMessage("");
  };

  const handleSave = async () => {
    if (!editingUser) return;

    const isNewlyVerifyingSeller =
      editingUser.role === "seller" &&
      editForm.is_verified &&
      !editingUser.is_verified;

    if (isNewlyVerifyingSeller && missingSellerBusinessFields(editingUser).length > 0) {
      setVerifyConfirmOpen(true);
      return;
    }

    await saveUser();
  };

  const saveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const original: Record<string, any> = {
        name: editingUser.name || "",
        role: editingUser.role || "",
        is_active: editingUser.is_active ?? true,
        restricted: editingUser.restricted ?? false,
        is_verified: editingUser.is_verified ?? false,
        verified_name: editingUser.verified_name || "",
      };
      const changed: Record<string, any> = {};
      for (const key of Object.keys(editForm)) {
        if (editForm[key] !== original[key]) changed[key] = editForm[key];
      }
      if (Object.keys(changed).length === 0) {
        setEditingUser(null);
        setVerifyConfirmOpen(false);
        return;
      }
      await adminService.updateUser(editingUser.id, changed);
      setEditingUser(null);
      setVerifyConfirmOpen(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkVerify = async () => {
    setBulkVerifying(true);
    try {
      const params: { role?: string } = {};
      if (role !== "all") params.role = role;
      await adminService.bulkVerifyUsers(params);
      setBulkVerifyConfirmOpen(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
    } finally {
      setBulkVerifying(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editingUser || newPassword.length < 8) return;
    setResetting(true);
    setResetMessage("");
    try {
      await adminService.resetUserPassword(editingUser.id, newPassword);
      setResetMessage("Password reset successfully");
      setNewPassword("");
    } catch (err: any) {
      setResetMessage(err?.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const handleCreditWallet = async () => {
    const amount = parseInt(creditAmount, 10);
    if (!editingUser || !amount || amount <= 0) return;
    setCrediting(true);
    setCreditMessage("");
    try {
      await adminService.creditUserWallet(editingUser.id, amount, creditReason.trim() || "Employee credited by admin");
      setCreditMessage(`${amount} credits added successfully`);
      setCreditAmount("");
    } catch (err: any) {
      setCreditMessage(err?.response?.data?.detail || "Failed to add credits");
    } finally {
      setCrediting(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        title="Users"
        subtitle="Manage all platform users"
        meta={`${data?.total ?? 0} total`}
        actions={<RefreshButton onRefresh={fetchUsers} />}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search name, email, phone, or WhatsApp..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-72"
        />
        <div className="flex gap-2">
          {ROLES.map((r) => (
            <button
              key={r}
              onClick={() => { setRole(r); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                role === r ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
              }`}
            >
              {r === "all" ? "All Roles" : r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>

        <select
          value={companyName}
          onChange={(e) => { setCompanyName(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        >
          <option value="">All Companies</option>
          {companyNames.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <div className="flex gap-2">
          {(["all", "verified", "unverified"] as const).map((v) => (
            <button
              key={v}
              onClick={() => { setVerifiedFilter(v); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                verifiedFilter === v ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
              }`}
            >
              {v === "all" ? "All Statuses" : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          {(["all", "restricted", "unrestricted"] as const).map((v) => (
            <button
              key={v}
              onClick={() => { setRestrictedFilter(v); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                restrictedFilter === v ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
              }`}
            >
              {v === "all" ? "All Users" : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        <button
          onClick={() => { setMarketPendingOnly((v) => !v); setPage(1); }}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            marketPendingOnly ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
          title="Show only users with a market verification request pending review"
        >
          Market Pending
        </button>

        <button
          onClick={() => { setPasswordPendingOnly((v) => !v); setPage(1); }}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            passwordPendingOnly ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
          title="Show only OTP-only users whose first password is waiting on admin approval"
        >
          Password Pending
        </button>

        <button
          onClick={() => { setSelfDeletedOnly((v) => !v); setPage(1); }}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            selfDeletedOnly ? "bg-rose-500/20 text-rose-400 border border-rose-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
          }`}
          title="Show only users who deleted their own account (restorable)"
        >
          Self-deleted
        </button>

        <DateRangeFilter
          fields={USER_DATE_FIELDS}
          value={dateFilters}
          onChange={(v) => { setDateFilters(v); setPage(1); }}
        />

        <div className="flex gap-2 ml-auto">
          <button
            onClick={handleBackfillLastLogin}
            disabled={backfillingLogin}
            title='Repairs accounts stuck showing "Never" under Last Login despite having actually signed in'
            className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {backfillingLogin ? "Fixing…" : "Fix Last Login"}
          </button>
          <button
            onClick={handleBackfillAuthMethod}
            disabled={backfillingAuthMethod}
            title='Repairs "—" under Signed Up Via / Last Login Via for accounts that predate this tracking'
            className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            {backfillingAuthMethod ? "Fixing…" : "Fix Auth Methods"}
          </button>
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={handleVerifySelected}
                disabled={bulkActionRunning}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 disabled:opacity-50 transition-colors"
              >
                {bulkActionRunning ? "Working…" : `Verify Selected (${selectedIds.size})`}
              </button>
              <button
                onClick={handleUnverifySelected}
                disabled={bulkActionRunning}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
              >
                {bulkActionRunning ? "Working…" : `Unverify Selected (${selectedIds.size})`}
              </button>
            </>
          )}
          {verifiedFilter !== "verified" && (
            <button
              onClick={() => setBulkVerifyConfirmOpen(true)}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 transition-colors"
            >
              Verify all{role !== "all" ? ` ${role}s` : ""}
            </button>
          )}
        </div>
      </div>

      {bulkVerifyConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setBulkVerifyConfirmOpen(false)}>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Verify all unverified{role !== "all" ? ` ${role}s` : " users"}?</h3>
            <p className="text-gray-400 text-sm mb-5">
              This marks every currently unverified{role !== "all" ? ` ${role}` : " user"} as verified and re-syncs their deals with the default "Gullivr Verified Seller" badge (or any custom verified name already set).
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setBulkVerifyConfirmOpen(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:bg-gray-800 transition-colors">Cancel</button>
              <button
                onClick={handleBulkVerify}
                disabled={bulkVerifying}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 disabled:opacity-50 transition-colors"
              >
                {bulkVerifying ? "Verifying..." : "Verify all"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 w-8">
                  <input
                    type="checkbox"
                    checked={(data?.items.length ?? 0) > 0 && data!.items.every((u) => selectedIds.has(u.id))}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-indigo-500 focus:ring-indigo-500"
                  />
                </th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Name</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Email</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Role</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Phone</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">WhatsApp</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Active Deals</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Last Login</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Auth Method</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Joined</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
              ) : data?.items.length === 0 ? (
                <tr><td colSpan={12} className="py-20 text-center text-gray-500">No users found</td></tr>
              ) : (
                data?.items.map((user) => (
                  <tr key={user.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(user.id)}
                        onChange={() => toggleSelected(user.id)}
                        className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-indigo-500 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div
                        className="flex items-center gap-3 cursor-pointer group"
                        onClick={() => setViewingUserId(user.id)}
                        title="Click to view full details"
                      >
                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-semibold text-xs group-hover:bg-indigo-500/40 transition-colors">
                          {user.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <span className="text-white font-medium group-hover:text-indigo-300 transition-colors">{user.name || "—"}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        user.role === "seller" ? "bg-cyan-500/10 text-cyan-400" :
                        user.role === "super_admin" ? "bg-amber-500/10 text-amber-400" :
                        "bg-emerald-500/10 text-emerald-400"
                      }`}>{user.role}</span>
                    </td>
                    <td className="py-3 px-4">
                      <PhoneDisplay countryCode={user.phone_country_code} number={user.phone_number} e164={user.phone} />
                    </td>
                    <td className="py-3 px-4">
                      <PhoneDisplay countryCode={user.whatsapp_country_code} number={user.whatsapp_number} e164={user.whatsapp} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        {user.account_disabled && user.disabled_by !== "admin" ? (
                          <span className="flex flex-col text-xs text-rose-400" title="User deleted their own account">
                            <span className="inline-flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                              Self-deleted
                            </span>
                            {user.disabled_at && (
                              <span className="pl-2.5 text-[11px] text-rose-300/80 whitespace-nowrap">
                                {formatISTDateTime(user.disabled_at)} IST
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-xs ${user.is_active !== false ? "text-green-400" : "text-red-400"}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${user.is_active !== false ? "bg-green-400" : "bg-red-400"}`} />
                            {user.is_active !== false ? "Active" : "Inactive"}
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 text-xs ${user.is_verified ? "text-blue-400" : "text-gray-500"}`}>
                          {user.is_verified ? "✔️ Verified" : "Unverified"}
                        </span>
                        {user.restricted && (
                          <span className="inline-flex items-center gap-1 text-xs text-orange-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                            Restricted
                          </span>
                        )}
                        {user.password_set_request?.status === "pending" && (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-amber-400"
                            title="Submitted a first password that needs admin approval — open View to approve or reject"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            Password pending
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {user.role === "seller" ? (
                        <div className="flex flex-col gap-0.5 items-start">
                          <button
                            onClick={() => navigate(`/admin/deals?dealer_id=${encodeURIComponent(user.id)}&is_active=true`)}
                            className="text-xs font-medium text-green-400 hover:underline"
                            title="View this seller's active deals"
                          >
                            {user.active_deals_count ?? 0} active
                          </button>
                          <button
                            onClick={() => navigate(`/admin/deals?dealer_id=${encodeURIComponent(user.id)}&is_active=false`)}
                            className="text-xs text-gray-500 hover:underline hover:text-gray-300"
                            title="View this seller's inactive deals"
                          >
                            {user.inactive_deals_count ?? 0} inactive
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {user.last_login_at ? formatISTDateTime(user.last_login_at) : "Never"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1 items-start">
                        <span title="Last login method"><AuthMethodBadge method={user.last_login_method} /></span>
                        {user.signup_method && user.signup_method !== user.last_login_method && (
                          <span className="text-[10px] text-gray-600">signed up via {AUTH_METHOD_LABELS[user.signup_method] || user.signup_method}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {user.created_at ? formatIST(user.created_at) : "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.account_disabled && user.disabled_by !== "admin" && (
                          confirmRestoreId === user.id ? (
                            <>
                              <button
                                onClick={() => restoreFromRow(user.id)}
                                disabled={restoringId === user.id}
                                className="px-2 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-md transition-colors"
                              >
                                {restoringId === user.id ? "Restoring…" : "Confirm restore"}
                              </button>
                              <button
                                onClick={() => setConfirmRestoreId(null)}
                                disabled={restoringId === user.id}
                                className="px-2 py-1 text-xs text-gray-400 hover:bg-gray-800 rounded-md transition-colors"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setConfirmRestoreId(user.id)}
                              className="px-2 py-1 text-xs text-green-400 hover:bg-green-500/10 rounded-md transition-colors"
                              title="Undo the user's self-delete: re-activate login and un-hide their deals"
                            >
                              Restore
                            </button>
                          )
                        )}
                        <button onClick={() => setActivityUser({ id: user.id, name: user.name })} className="px-2 py-1 text-xs text-emerald-400 hover:bg-emerald-500/10 rounded-md transition-colors">Activity</button>
                        {user.role === "seller" && (
                          <button onClick={() => setMarketCoverageUser({ id: user.id, name: user.name })} className="px-2 py-1 text-xs text-cyan-400 hover:bg-cyan-500/10 rounded-md transition-colors">Market Map</button>
                        )}
                        <button onClick={() => setOnboardingUser(user)} className={`px-2 py-1 text-xs rounded-md transition-colors ${user.onboarding?.steps?.length > 0 ? "text-amber-400 hover:bg-amber-500/10" : "text-gray-500 hover:bg-gray-800"}`}>{user.onboarding?.steps?.length > 0 ? "Onboarding" : "No Onboarding"}</button>
                        <button
                          onClick={() => setNumberChangeUserId(user.id)}
                          title="Change the account's WhatsApp / login number"
                          className={`px-2 py-1 text-xs rounded-md transition-colors ${user.pending_phone_change_request ? "text-amber-300 bg-amber-500/10 hover:bg-amber-500/20" : "text-sky-400 hover:bg-sky-500/10"}`}
                        >
                          {user.pending_phone_change_request ? "Number ●" : "Number"}
                        </button>
                        <button onClick={() => setViewingUserId(user.id)} className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors">View</button>
                        <button onClick={() => setDeleteTarget(user)} className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* User Detail Modal */}
      {viewingUserId && (
        <UserDetailModal
          userId={viewingUserId}
          onClose={() => setViewingUserId(null)}
          onEdit={(u) => { setViewingUserId(null); handleEdit(u); }}
          onChanged={fetchUsers}
        />
      )}

      {/* User Activity Modal */}
      {activityUser && (
        <UserActivityModal userId={activityUser.id} userName={activityUser.name} onClose={() => setActivityUser(null)} />
      )}

      {/* Seller Market Map Modal */}
      {marketCoverageUser && (
        <UserMarketCoverageModal userId={marketCoverageUser.id} userName={marketCoverageUser.name} onClose={() => setMarketCoverageUser(null)} />
      )}

      {/* Change Account Number Modal — also opened by ?number_change_user=<id>
          from the notification bell's Number Change tab. */}
      {numberChangeUserId && (
        <UserNumberChangeModal
          userId={numberChangeUserId}
          onClose={() => setNumberChangeUserId(null)}
          onChanged={fetchUsers}
        />
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-lg font-semibold text-white mb-1">Edit User</h2>
            <p className="text-xs text-gray-500 mb-4">{editingUser.email}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Role</label>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    editingUser?.role === "seller" ? "bg-cyan-500/10 text-cyan-400" :
                    editingUser?.role === "super_admin" ? "bg-amber-500/10 text-amber-400" :
                    "bg-indigo-500/10 text-indigo-400"
                  }`}>{editingUser?.role}</span>
                  {(editingUser?.role === "seller" || editingUser?.role === "buyer") && (
                    <button
                      onClick={() => setRoleChangeTarget({ user: editingUser, newRole: editingUser.role === "seller" ? "buyer" : "seller" })}
                      className="px-3 py-1.5 bg-orange-600/20 text-orange-400 border border-orange-600/30 rounded-lg text-xs hover:bg-orange-600/30 transition-colors"
                    >
                      Switch to {editingUser.role === "seller" ? "Buyer" : "Seller"}
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-gray-600 mt-1">Role change wipes all profile data — use the button above</p>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-indigo-500 focus:ring-indigo-500" />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={editForm.is_verified} onChange={(e) => setEditForm({ ...editForm, is_verified: e.target.checked })} className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-indigo-500 focus:ring-indigo-500" />
                  Verified
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input type="checkbox" checked={editForm.restricted} onChange={(e) => setEditForm({ ...editForm, restricted: e.target.checked })} className="w-4 h-4 rounded bg-gray-800 border-gray-700 text-orange-500 focus:ring-orange-500" />
                  Restricted
                </label>
              </div>
              {editForm.restricted && (
                <p className="text-[11px] text-orange-400 -mt-4">
                  Restricted users are blocked from logging in until an admin unrestricts them.
                </p>
              )}

              {editForm.is_verified && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Verified Badge Name</label>
                  <input
                    value={editForm.verified_name}
                    onChange={(e) => setEditForm({ ...editForm, verified_name: e.target.value })}
                    placeholder="Gullivr Verified Seller"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                  <p className="text-[10px] text-gray-600 mt-1">Shown next to the verified badge on this seller's deals. Leave blank to use the default above.</p>
                </div>
              )}

              {/* Password Reset */}
              <div className="pt-3 border-t border-gray-800">
                <label className="block text-sm text-gray-400 mb-2">Reset Password</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setResetMessage(""); }}
                    placeholder="New password (min 8 chars)"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm"
                  />
                  <button
                    onClick={handleResetPassword}
                    disabled={resetting || newPassword.length < 8}
                    className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-500 transition-colors text-sm disabled:opacity-40 whitespace-nowrap"
                  >
                    {resetting ? "Saving..." : "Set Password"}
                  </button>
                </div>
                {resetMessage && (
                  <p className={`text-xs mt-2 ${resetMessage.includes("success") ? "text-green-400" : "text-red-400"}`}>
                    {resetMessage}
                  </p>
                )}
              </div>

              {/* Credit Wallet */}
              <div className="pt-3 border-t border-gray-800">
                <label className="block text-sm text-gray-400 mb-2">Add Wallet Credits</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={1}
                    value={creditAmount}
                    onChange={(e) => { setCreditAmount(e.target.value); setCreditMessage(""); }}
                    placeholder="Credits"
                    className="w-28 px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                  />
                  <input
                    type="text"
                    value={creditReason}
                    onChange={(e) => { setCreditReason(e.target.value); setCreditMessage(""); }}
                    placeholder="Reason"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 text-sm"
                  />
                  <button
                    onClick={handleCreditWallet}
                    disabled={crediting || !creditAmount || parseInt(creditAmount, 10) <= 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-500 transition-colors text-sm disabled:opacity-40 whitespace-nowrap"
                  >
                    {crediting ? "Adding..." : "Add Credits"}
                  </button>
                </div>
                {creditMessage && (
                  <p className={`text-xs mt-2 ${creditMessage.includes("success") ? "text-green-400" : "text-red-400"}`}>
                    {creditMessage}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingUser(null)} disabled={saving} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm disabled:opacity-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed">{saving ? "Saving..." : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal — z-[60] so it renders above edit modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-900 border border-red-900/50 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Delete User</h2>
                <p className="text-sm text-gray-400 mt-0.5">{deleteTarget.name} · {deleteTarget.email}</p>
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-3">Confirming will permanently:</p>
            <ul className="space-y-1.5 mb-5">
              {[
                "Remove user account from database",
                "Delete profile image from S3",
                "Delete all deals and inventory (typed + legacy collections)",
                "Delete all contacts, enquiries, and leads",
                "Delete all analytics events and activity history",
                "Delete all AI media logs and generated assets",
                "Delete all community posts, comments, and reactions",
                "Delete all RAG sessions and conversation history",
                "Delete all quota and subscription records",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-gray-400">
                  <span className="text-red-400 mt-0.5 shrink-0">✕</span>{item}
                </li>
              ))}
            </ul>
            <p className="text-xs text-red-400 font-medium mb-4">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm disabled:opacity-50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-500 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">{deleting ? "Deleting..." : "Delete Permanently"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Confirm Modal — z-[60] so it renders above edit modal */}
      {roleChangeTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-900 border border-orange-900/50 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Change Role</h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  {roleChangeTarget.user.name} · {roleChangeTarget.user.role}{" "}
                  <span className="text-orange-400">→</span>{" "}
                  {roleChangeTarget.newRole}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-3">Confirming will:</p>
            <ul className="space-y-1.5 mb-3">
              {[
                { keep: true, text: `Set role to "${roleChangeTarget.newRole}" — requires fresh onboarding` },
                { keep: true, text: "Keep: name, email, password, profile image" },
                { keep: true, text: "Keep: phone, business phone, WhatsApp (+ country codes), phone/email verified" },
                { keep: true, text: "Keep: GST/VAT number, office address, company name, company email" },
                { keep: true, text: "Keep: PAN, business registration, employee count, years in business" },
                { keep: true, text: "Keep: website, language" },
                { keep: false, text: "Delete: seller/buyer-specific profile (seller types, specializations, coverage, buyer preferences)" },
                { keep: false, text: "Delete: all deals, inventory, contacts, and AI-generated media" },
                { keep: false, text: "Delete: analytics events, RAG sessions, community posts" },
                { keep: false, text: "Delete: quota usage, subscription records, notification tokens" },
                { keep: false, text: "Delete: saved deals, leads, feed metadata, enquiry drafts" },
              ].map(({ keep, text }) => (
                <li key={text} className={`flex items-start gap-2 text-xs ${keep ? "text-green-400" : "text-orange-300"}`}>
                  <span className="mt-0.5 shrink-0">{keep ? "✓" : "✕"}</span>{text}
                </li>
              ))}
            </ul>
            <p className="text-xs text-orange-400 font-medium mb-4">All role-specific data will be permanently wiped. This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRoleChangeTarget(null)} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm">Cancel</button>
              <button onClick={handleChangeRole} disabled={roleChanging} className="px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-500 transition-colors text-sm font-medium disabled:opacity-50">
                {roleChanging ? "Changing…" : `Confirm — Switch to ${roleChangeTarget.newRole}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verify-without-GST/PAN Confirm Modal — z-[60] so it renders above edit modal */}
      {verifyConfirmOpen && editingUser && (
        <ConfirmModal
          title="Verify Seller"
          description={`${editingUser.name || editingUser.email} is good to go, but these optional business-profile fields were left empty/unverified:`}
          items={missingSellerBusinessFields(editingUser)}
          confirmLabel="Verify Anyway"
          confirmClass="bg-indigo-600 hover:bg-indigo-500"
          irreversible={false}
          onConfirm={saveUser}
          onCancel={() => setVerifyConfirmOpen(false)}
        />
      )}
      {onboardingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-5xl shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold text-white">Onboarding Data</h2>
                <p className="text-sm text-gray-400">{onboardingUser.name || onboardingUser.email}</p>
              </div>
              <button onClick={() => setOnboardingUser(null)} className="text-gray-500 hover:text-white transition-colors">✕</button>
            </div>
            
            <div className="space-y-6 mb-6">
              {(!onboardingUser.onboarding?.steps || onboardingUser.onboarding.steps.length === 0) ? (
                <p className="text-sm text-gray-500 italic">No onboarding steps completed yet.</p>
              ) : (
                onboardingUser.onboarding.steps.map((step: any, idx: number) => (
                  <div key={idx} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
                    <h3 className="text-md font-medium text-white mb-2 pb-2 border-b border-gray-700/50 flex justify-between">
                      {step.key}
                      {step.skipped ? <span className="text-xs text-yellow-400">Skipped</span> : step.completed ? <span className="text-xs text-green-400">Completed</span> : <span className="text-xs text-amber-400">Incomplete</span>}
                    </h3>
                    
                    {!step.completed && !step.skipped && step.missing_fields && step.missing_fields.length > 0 && (
                      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs">
                        <strong className="block mb-1 text-red-300">Missing required fields:</strong> 
                        {step.missing_fields.join(", ")}
                      </div>
                    )}

                    {step.data && Object.keys(step.data).length > 0 ? (
                      <div className="grid grid-cols-2 gap-6">
                        {Object.entries(step.data).map(([k, v]: [string, any]) => {
                          // For GST verification, if it has a 'raw' field from the backend, we show the full raw API response
                          const displayObj = (k === 'gst_verification' && v && typeof v === 'object' && 'raw' in v) ? v.raw : v;
                          
                          // Attempt to extract the GSTIN so we can show the "Fetch Live" button
                          let gstinForFetch = "";
                          if (k === 'gst_verification' && v && v.gstin) gstinForFetch = v.gstin;
                          if (k === 'gst_vat_number' && Array.isArray(v) && (v[0]?.gst_number || v[0]?.tax_number)) gstinForFetch = v[0].gst_number || v[0].tax_number;

                          return (
                            <div key={k} className="flex flex-col relative">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-500 capitalize">{k.replace(/_/g, " ")}</span>
                                {gstinForFetch && (
                                  <button
                                    onClick={async () => {
                                      setFetchingGst(true);
                                      try {
                                        const res = await adminService.verifyGstDetails(gstinForFetch);
                                        setLiveGstDetails({ gstin: gstinForFetch, stepKey: step.key, data: res });
                                      } catch (err: any) {
                                        const detail = err?.response?.data?.detail;
                                        showAlert(typeof detail === "string" ? detail : "Failed to fetch live GST details");
                                      } finally {
                                        setFetchingGst(false);
                                      }
                                    }}
                                    disabled={fetchingGst}
                                    className="px-2.5 py-1 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 text-[10px] font-medium rounded-lg border border-indigo-600/30 transition-colors disabled:opacity-50"
                                  >
                                    {fetchingGst ? "Fetching..." : "Fetch Live Details"}
                                  </button>
                                )}
                              </div>
                              <div className="text-sm text-gray-300">
                                <RenderOnboardingValue value={displayObj} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No data recorded for this step.</p>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
              <button onClick={() => setOnboardingUser(null)} className="px-4 py-2 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 transition-colors text-sm">Close</button>
              {!onboardingUser.is_verified && (
                <button 
                  onClick={async () => {
                    setVerifyingUser(true);
                    try {
                      await adminService.updateUser(onboardingUser.id, { is_verified: true });
                      setOnboardingUser(null);
                      fetchUsers();
                    } catch (err: any) {
                      const detail = err?.response?.data?.detail;
                      showAlert(typeof detail === "string" ? detail : "Error verifying user");
                    } finally {
                      setVerifyingUser(false);
                    }
                  }} 
                  disabled={verifyingUser}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-500 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {verifyingUser ? "Verifying..." : "Verify User"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Live GST Details Modal */}
      {liveGstDetails && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setLiveGstDetails(null)}>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-4xl shadow-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4 shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-white">Live GST Details</h2>
                <p className="text-xs text-gray-400 mt-1">{liveGstDetails.gstin} (fetched from GST API directly)</p>
              </div>
              <button onClick={() => setLiveGstDetails(null)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-lg font-bold">×</button>
            </div>
            <div className="overflow-y-auto flex-1 bg-gray-800/50 border border-gray-700 rounded-xl p-4">
              <pre className="text-xs text-gray-300 whitespace-pre-wrap break-all">
                {JSON.stringify(liveGstDetails.data, null, 2)}
              </pre>
            </div>
            <div className="shrink-0 pt-4 mt-2 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-3">
                Confirming re-verifies this GSTIN against the live GST portal and upserts this data onto the seller's profile — same as approving a GST Verification Job, but works even when this user has no job record yet.
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setLiveGstDetails(null)} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white rounded-md transition-colors">Cancel</button>
                <button
                  onClick={async () => {
                    setConfirmingGst(true);
                    try {
                      await adminService.confirmGstVerification({
                        user_id: onboardingUser.id,
                        gstin: liveGstDetails.gstin,
                        step_key: liveGstDetails.stepKey,
                      });
                      const refreshed = await adminService.getUser(onboardingUser.id);
                      setOnboardingUser(refreshed);
                      setLiveGstDetails(null);
                      fetchUsers();
                    } catch (err: any) {
                      const detail = err?.response?.data?.detail;
                      showAlert(typeof detail === "string" ? detail : "Failed to verify GSTIN");
                    } finally {
                      setConfirmingGst(false);
                    }
                  }}
                  disabled={confirmingGst}
                  className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 text-white rounded-md transition-colors disabled:opacity-50"
                >
                  {confirmingGst ? "Verifying…" : "Confirm & Verify"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {alertModal}
    </div>
  );
};

export default AdminUsersPage;
